import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';
import {
  Activity,
  AlertTriangle,
  Boxes,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Cloud,
  Crosshair,
  Database,
  Download,
  Eye,
  Flame,
  GitBranch,
  GitCompareArrows,
  History,
  Layers3,
  Map as MapIcon,
  Maximize2,
  Minimize2,
  Network,
  Radar,
  Route,
  RotateCcw,
  Search,
  Server,
  ShieldCheck,
  Sparkles,
  Target,
  UserRound,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { apiFetch, getApiBase } from '../lib/apiClient';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { NativeResponseActions } from './NativeResponseActions';
import {
  countSentinelSeeds,
  resolveEstateDrilldownKeys,
  resolveSentinelLensKeys,
  type MobEstateDrilldownRequest,
  type MobSentinelScope,
  type MobSentinelSeverity,
} from '../lib/mainOperatingBaseInteractions';

interface MobMonitoring {
  status: 'monitored' | 'partial' | 'blind' | 'not_applicable';
  coveragePercent: number;
  signals: string[];
  expectedSignals: string[];
  gaps: string[];
}

interface MobRisk {
  score: number;
  level: 'Low' | 'Medium' | 'High' | 'Critical';
  baseScore: number;
  contributors: string[];
}

interface MobEstateMetadata {
  canonicalEntityId: string;
  canonicalKey: string | null;
  sourceGraphRunId: string;
  providers: string[];
  providerPresence: Record<string, unknown>;
  confidence: number;
  matchStrategy: string;
  evidenceCount: number;
  authoritativeEvidenceCount: number;
  conflicts: unknown[];
  lastObservedAt: string | null;
  freshness: 'fresh' | 'recent' | 'stale' | 'unknown';
  freshnessAgeMinutes: number | null;
  providerFreshness: Record<string, unknown>;
}

export interface MobNode {
  key: string;
  sourceKey?: string | null;
  type: string;
  label: string;
  zone: string;
  properties: Record<string, unknown>;
  risk: MobRisk;
  monitoring: MobMonitoring;
  criticality: 'Low' | 'Medium' | 'High' | 'Critical' | null;
  crownJewel: boolean;
  threatState: 'normal' | 'elevated' | 'active_threat' | 'exposed';
  firstSeen: string | null;
  lastSeen: string | null;
  sourceTables: string[];
  estate?: MobEstateMetadata;
}

export interface MobEdge {
  key: string;
  from: string;
  to: string;
  type: string;
  label: string;
  confidence: number;
  evidenceCount: number;
  provenance: 'observed' | 'declared' | 'derived';
  firstSeen: string | null;
  lastSeen: string | null;
  sourceTables: string[];
}

interface MobZone {
  id: string;
  label: string;
  description: string;
  order: number;
  nodeCount: number;
  highRiskCount: number;
  crownJewelCount: number;
  monitoredCount: number;
  coveragePercent: number;
}

export interface MobSnapshot {
  id: string;
  customerId: string;
  customerName: string;
  sourceFobSnapshotId: string;
  sourceEstateKnowledgeGraphRunId?: string | null;
  sourceEstateKnowledgeGraphResolverVersion?: string | null;
  sourceEstateKnowledgeGraphCompletedAt?: string | null;
  status: string;
  generatedAt: string | null;
  completedAt: string | null;
  summary: Record<string, unknown>;
  zones: MobZone[];
  nodes: MobNode[];
  edges: MobEdge[];
  sourceHealth: Array<Record<string, unknown>>;
  diagnostics: string[];
  createdBy?: string | null;
}

export interface MobSnapshotSummary {
  id: string;
  sourceFobSnapshotId: string;
  sourceEstateKnowledgeGraphRunId?: string | null;
  sourceEstateKnowledgeGraphResolverVersion?: string | null;
  sourceEstateKnowledgeGraphCompletedAt?: string | null;
  status: string;
  generatedAt: string | null;
  completedAt: string | null;
  summary: Record<string, unknown>;
}

export interface AiC2MobOverlay {
  version: number;
  missionId: string;
  caseRef: string;
  customerId: string;
  sourceFobSnapshotId?: string | null;
  createdAt?: string;
  entities: Array<{ type: string; value: string; status: string }>;
  timeline?: Array<{ time: string; sortKey?: number; text: string; category: string }>;
}

type LensId = 'architecture' | 'monitoring' | 'risk' | 'threats' | 'identity' | 'network' | 'exposure' | 'sentinel';
type DensityId = 'executive' | 'balanced' | 'detailed';
type CanvasMode = 'architecture' | 'intelligence';
type RelationKind = 'authentication' | 'network' | 'process' | 'threat' | 'architecture' | 'other';
type LayoutNode = MobNode & { x: number; y: number; width: number; height: number; hop?: number };
type LayoutZone = MobZone & { x: number; y: number; width: number; height: number; clipped: number };
type ActivityState = 'live' | 'recent' | 'historical';
type DiffStatus = 'new' | 'changed' | 'unchanged';

type GraphLayout = {
  nodes: LayoutNode[];
  zones: LayoutZone[];
  edges: MobEdge[];
  clipped: number;
  page: { page: number; pageSize: number; total: number; start: number; end: number; pages: number } | null;
};

type DiffSummary = {
  newNodes: Set<string>;
  removedNodes: Set<string>;
  changedNodes: Set<string>;
  newEdges: Set<string>;
  removedEdges: Set<string>;
  changedEdges: Set<string>;
};

type CrownPath = { targetKey: string; targetLabel: string; nodeKeys: string[]; edgeKeys: string[] };

const BASE_WIDTH = 1600;
const BASE_HEIGHT = 870;
const ZONE_GEOMETRY: Record<string, { x: number; y: number; width: number; height: number }> = {
  identity: { x: 28, y: 38, width: 350, height: 360 },
  azure: { x: 400, y: 38, width: 520, height: 410 },
  security: { x: 942, y: 38, width: 310, height: 410 },
  threat: { x: 1274, y: 38, width: 300, height: 410 },
  onprem: { x: 28, y: 475, width: 520, height: 360 },
  telemetry: { x: 570, y: 475, width: 420, height: 360 },
  external: { x: 1012, y: 475, width: 562, height: 360 },
};
const FOCUSED_GEOMETRY = { x: 30, y: 72, width: 1540, height: 752 };
const STARS = Array.from({ length: 145 }, (_, index) => ({
  x: 12 + ((index * 101) % 1570),
  y: 10 + ((index * 61) % 840),
  r: 0.45 + ((index * 7) % 7) / 8,
  opacity: 0.12 + ((index * 17) % 46) / 100,
}));

const LENSES: Array<{ id: LensId; label: string; capability: string; description: string }> = [
  { id: 'architecture', label: 'Architecture', capability: 'view_architecture', description: 'Strategic live environment model.' },
  { id: 'monitoring', label: 'Monitoring', capability: 'view_monitoring_overlay', description: 'Observed coverage and blind spots.' },
  { id: 'risk', label: 'Risk', capability: 'view_risk_overlay', description: 'Explainable composite security risk.' },
  { id: 'threats', label: 'Threats', capability: 'view_threat_overlay', description: 'Incidents, alerts, IoCs and exposures.' },
  { id: 'identity', label: 'Identity', capability: 'view_identity_paths', description: 'Identity and authentication surfaces.' },
  { id: 'network', label: 'Network', capability: 'view_network_paths', description: 'Observed communications and infrastructure paths.' },
  { id: 'exposure', label: 'Exposure', capability: 'view_exposure', description: 'Public, vulnerable and crown-jewel exposure.' },
  { id: 'sentinel', label: 'Sentinel', capability: 'view_sentinel_graph_lens', description: 'Persisted Microsoft Sentinel incidents, alerts and canonically related entities.' },
];

const RELATION_LABELS: Record<RelationKind, string> = {
  authentication: 'Authentication',
  network: 'Network',
  process: 'Process / File',
  threat: 'Threat / Exposure',
  architecture: 'Architecture',
  other: 'Other',
};

function n(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalize(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function compact(value: number): string {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

function formatTime(value: string | null | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : String(value);
}

function riskClass(score: number): string {
  if (score >= 85) return 'text-rose-300';
  if (score >= 65) return 'text-orange-300';
  if (score >= 35) return 'text-amber-300';
  return 'text-emerald-300';
}

function monitoringClass(status: MobMonitoring['status']): string {
  if (status === 'monitored') return 'border-emerald-400/40 bg-emerald-500/12 text-emerald-200';
  if (status === 'partial') return 'border-amber-400/40 bg-amber-500/12 text-amber-200';
  if (status === 'blind') return 'border-rose-400/45 bg-rose-500/12 text-rose-200';
  return 'border-slate-500/30 bg-slate-800/40 text-slate-400';
}

const PROVIDER_LABELS: Record<string, { short: string; label: string; dot: string; chip: string }> = {
  azure: { short: 'AZ', label: 'Azure', dot: 'rgb(56,189,248)', chip: 'border-sky-400/30 bg-sky-500/10 text-sky-200' },
  entra: { short: 'ID', label: 'Entra', dot: 'rgb(167,139,250)', chip: 'border-violet-400/30 bg-violet-500/10 text-violet-200' },
  intune: { short: 'IN', label: 'Intune', dot: 'rgb(34,211,238)', chip: 'border-cyan-400/30 bg-cyan-500/10 text-cyan-200' },
  mde: { short: 'MDE', label: 'MDE', dot: 'rgb(52,211,153)', chip: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' },
  sentinel: { short: 'SI', label: 'Sentinel', dot: 'rgb(251,113,133)', chip: 'border-rose-400/30 bg-rose-500/10 text-rose-200' },
  sentinel_logs: { short: 'LOG', label: 'Sentinel telemetry', dot: 'rgb(251,191,36)', chip: 'border-amber-400/30 bg-amber-500/10 text-amber-200' },
};

function providerMeta(provider: string) {
  return PROVIDER_LABELS[provider] ?? { short: provider.slice(0, 3).toUpperCase(), label: provider.replaceAll('_', ' '), dot: 'rgb(148,163,184)', chip: 'border-slate-600 bg-slate-800/50 text-slate-300' };
}

function freshnessClass(status: MobEstateMetadata['freshness']): string {
  if (status === 'fresh') return 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200';
  if (status === 'recent') return 'border-cyan-400/30 bg-cyan-500/10 text-cyan-200';
  if (status === 'stale') return 'border-rose-400/30 bg-rose-500/10 text-rose-200';
  return 'border-slate-600 bg-slate-800/50 text-slate-400';
}

function nodeIcon(type: string) {
  if (type === 'identity') return UserRound;
  if (type === 'endpoint') return Server;
  if (type === 'network_device' || type === 'ip') return Network;
  if (type === 'security_control') return ShieldCheck;
  if (['subscription', 'resource_group', 'workspace', 'cloud_resource'].includes(type)) return Cloud;
  if (type === 'data_source') return Database;
  if (['incident', 'alert', 'ioc', 'cve'].includes(type)) return AlertTriangle;
  return Boxes;
}

function safeEntries(value: Record<string, unknown>): Array<[string, string]> {
  return Object.entries(value)
    .filter(([, item]) => item !== null && item !== undefined && item !== '')
    .slice(0, 22)
    .map(([key, item]) => [key, typeof item === 'object' ? JSON.stringify(item) : String(item)]);
}

function isPublicNode(node: MobNode): boolean {
  return node.zone === 'external' || normalize(node.properties['scope']) === 'public';
}

function relationKind(edge: MobEdge): RelationKind {
  const value = `${edge.type} ${edge.label}`.toLowerCase();
  if (/auth|logon|login|signin|credential|member|role|identity/.test(value)) return 'authentication';
  if (/communicat|network|ip|port|dns|route|flow|connect|public/.test(value)) return 'network';
  if (/process|execute|spawn|parent|file|image|module|service|task/.test(value)) return 'process';
  if (/incident|alert|ioc|indicator|cve|vulnerab|threat|affected|exposure/.test(value)) return 'threat';
  if (/contain|represent|workspace|resource|telemetry|source|subscription|group|tenant/.test(value)) return 'architecture';
  return 'other';
}

function activityState(edge: MobEdge, generatedAt: string | null): ActivityState {
  if (!edge.lastSeen || !generatedAt) return 'historical';
  const end = new Date(generatedAt).getTime();
  const seen = new Date(edge.lastSeen).getTime();
  if (!Number.isFinite(end) || !Number.isFinite(seen)) return 'historical';
  const hours = Math.max(0, end - seen) / 3_600_000;
  if (hours <= 1) return 'live';
  if (hours <= 24) return 'recent';
  return 'historical';
}

function lensNodes(nodes: MobNode[], edges: MobEdge[], lens: LensId, aiKeys: Set<string>, sentinelScope: MobSentinelScope, sentinelSeverity: MobSentinelSeverity): MobNode[] {
  if (lens === 'architecture') return nodes;
  if (lens === 'sentinel') {
    const sentinel = resolveSentinelLensKeys(nodes, edges, sentinelScope, sentinelSeverity);
    return nodes.filter((node) => sentinel.keys.has(node.key));
  }
  if (lens === 'monitoring') return nodes.filter((node) => ['blind', 'partial', 'monitored'].includes(node.monitoring.status) || node.crownJewel);
  if (lens === 'risk') return nodes.filter((node) => node.risk.score >= 25 || node.crownJewel || aiKeys.has(node.key));
  if (lens === 'threats') return nodes.filter((node) => node.threatState !== 'normal' || aiKeys.has(node.key) || node.risk.score >= 65);
  if (lens === 'identity') {
    const identity = new Set(nodes.filter((node) => node.zone === 'identity' || node.type === 'identity').map((node) => node.key));
    for (const edge of edges) if (identity.has(edge.from) || identity.has(edge.to)) { identity.add(edge.from); identity.add(edge.to); }
    return nodes.filter((node) => identity.has(node.key));
  }
  if (lens === 'network') return nodes.filter((node) => ['network_device', 'ip', 'endpoint', 'cloud_resource', 'security_control', 'workspace'].includes(node.type));
  return nodes.filter((node) => node.type === 'cve' || node.type === 'ioc' || isPublicNode(node) || node.crownJewel || node.monitoring.status === 'blind');
}

function nodePriority(node: MobNode, degree: Map<string, number>, lens: LensId, aiKeys: Set<string>, pinnedKeys: Set<string>, drilldownKeys: Set<string>): number {
  let score = 0;
  if (pinnedKeys.has(node.key)) score += 1_000_000;
  if (drilldownKeys.has(node.key)) score += 500_000;
  if (aiKeys.has(node.key)) score += 200_000;
  if (node.crownJewel) score += 100_000;
  if (lens === 'monitoring') score += node.monitoring.status === 'blind' ? 70_000 : node.monitoring.status === 'partial' ? 40_000 : 0;
  if (node.threatState === 'active_threat') score += 60_000;
  if (node.threatState === 'exposed') score += 30_000;
  score += node.risk.score * 350;
  score += Math.min(25_000, (degree.get(node.key) ?? 0) * 35);
  return score;
}

function buildDegree(edges: MobEdge[]): Map<string, number> {
  const degree = new Map<string, number>();
  for (const edge of edges) {
    const weight = Math.min(25, edge.evidenceCount || 1);
    degree.set(edge.from, (degree.get(edge.from) ?? 0) + weight);
    degree.set(edge.to, (degree.get(edge.to) ?? 0) + weight);
  }
  return degree;
}

function layoutArchitecture(input: {
  nodes: MobNode[];
  edges: MobEdge[];
  zones: MobZone[];
  lens: LensId;
  density: DensityId;
  aiKeys: Set<string>;
  pinnedKeys: Set<string>;
  drilldownKeys: Set<string>;
  sentinelScope: MobSentinelScope;
  sentinelSeverity: MobSentinelSeverity;
  focusedZoneId: string;
  focusedPage: number;
}): GraphLayout {
  const candidates = lensNodes(input.nodes, input.edges, input.lens, input.aiKeys, input.sentinelScope, input.sentinelSeverity);
  const degree = buildDegree(input.edges);
  const layoutNodes: LayoutNode[] = [];
  const layoutZones: LayoutZone[] = [];
  const selectedKeys = new Set<string>();
  let clipped = 0;

  if (input.focusedZoneId) {
    const zone = input.zones.find((item) => item.id === input.focusedZoneId);
    if (!zone) return { nodes: [], zones: [], edges: [], clipped: 0, page: null };
    const rows = candidates
      .filter((node) => node.zone === input.focusedZoneId)
      .sort((a, b) => nodePriority(b, degree, input.lens, input.aiKeys, input.pinnedKeys, input.drilldownKeys) - nodePriority(a, degree, input.lens, input.aiKeys, input.pinnedKeys, input.drilldownKeys) || a.label.localeCompare(b.label));
    const nodeWidth = input.density === 'detailed' ? 116 : input.density === 'executive' ? 176 : 138;
    const nodeHeight = input.density === 'executive' ? 58 : 49;
    const gapX = input.density === 'detailed' ? 8 : 12;
    const gapY = 10;
    const cols = Math.max(1, Math.floor((FOCUSED_GEOMETRY.width - 36) / (nodeWidth + gapX)));
    const rowsAvailable = Math.max(1, Math.floor((FOCUSED_GEOMETRY.height - 92) / (nodeHeight + gapY)));
    const naturalPage = cols * rowsAvailable;
    const pageSize = input.density === 'executive' ? Math.min(48, naturalPage) : input.density === 'detailed' ? Math.min(132, naturalPage) : Math.min(96, naturalPage);
    const pages = Math.max(1, Math.ceil(rows.length / Math.max(1, pageSize)));
    const page = Math.max(0, Math.min(input.focusedPage, pages - 1));
    const start = page * pageSize;
    const selected = rows.slice(start, start + pageSize);
    selected.forEach((node, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = FOCUSED_GEOMETRY.x + 18 + nodeWidth / 2 + col * (nodeWidth + gapX);
      const y = FOCUSED_GEOMETRY.y + 72 + nodeHeight / 2 + row * (nodeHeight + gapY);
      layoutNodes.push({ ...node, x, y, width: nodeWidth, height: nodeHeight });
      selectedKeys.add(node.key);
    });
    clipped = Math.max(0, rows.length - selected.length);
    layoutZones.push({ ...zone, ...FOCUSED_GEOMETRY, clipped });
    const selectedEdges = input.edges
      .filter((edge) => selectedKeys.has(edge.from) && selectedKeys.has(edge.to))
      .sort((a, b) => b.evidenceCount - a.evidenceCount)
      .slice(0, input.density === 'detailed' ? 900 : 600);
    return {
      nodes: layoutNodes,
      zones: layoutZones,
      edges: selectedEdges,
      clipped,
      page: { page, pageSize, total: rows.length, start: rows.length ? start + 1 : 0, end: Math.min(rows.length, start + pageSize), pages },
    };
  }

  const densityFactor = input.density === 'executive' ? 0.55 : input.density === 'detailed' ? 1.75 : 1;
  for (const zone of input.zones) {
    const geometry = ZONE_GEOMETRY[zone.id];
    if (!geometry) continue;
    const rows = candidates
      .filter((node) => node.zone === zone.id)
      .sort((a, b) => nodePriority(b, degree, input.lens, input.aiKeys, input.pinnedKeys, input.drilldownKeys) - nodePriority(a, degree, input.lens, input.aiKeys, input.pinnedKeys, input.drilldownKeys) || a.label.localeCompare(b.label));
    const nodeWidth = input.density === 'detailed' ? (geometry.width >= 480 ? 128 : 112) : input.density === 'executive' ? (geometry.width >= 480 ? 174 : 145) : geometry.width >= 480 ? 150 : geometry.width >= 400 ? 142 : 126;
    const nodeHeight = input.density === 'executive' ? 57 : 52;
    const gapX = input.density === 'detailed' ? 8 : 16;
    const gapY = input.density === 'detailed' ? 9 : 14;
    const cols = Math.max(1, Math.floor((geometry.width - 32) / (nodeWidth + gapX)));
    const rowsAvailable = Math.max(1, Math.floor((geometry.height - 82) / (nodeHeight + gapY)));
    const capacity = Math.max(1, Math.floor(cols * rowsAvailable * densityFactor));
    const selected = rows.slice(0, capacity);
    clipped += Math.max(0, rows.length - selected.length);
    layoutZones.push({ ...zone, ...geometry, clipped: Math.max(0, rows.length - selected.length) });
    selected.forEach((node, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = geometry.x + 18 + nodeWidth / 2 + col * (nodeWidth + gapX);
      const y = geometry.y + 72 + nodeHeight / 2 + row * (nodeHeight + gapY);
      layoutNodes.push({ ...node, x, y, width: nodeWidth, height: nodeHeight });
      selectedKeys.add(node.key);
    });
  }
  const selectedEdges = input.edges
    .filter((edge) => selectedKeys.has(edge.from) && selectedKeys.has(edge.to))
    .filter((edge) => input.lens !== 'network' || ['communicated_with', 'has_ip', 'has_public_ip', 'feeds_workspace', 'telemetry_source', 'represents', 'contains'].includes(edge.type))
    .sort((a, b) => Number(b.provenance === 'observed') - Number(a.provenance === 'observed') || b.evidenceCount - a.evidenceCount)
    .slice(0, input.density === 'detailed' ? 700 : input.density === 'executive' ? 240 : 420);
  return { nodes: layoutNodes, zones: layoutZones, edges: selectedEdges, clipped, page: null };
}

function buildAdjacency(edges: MobEdge[], enabledKinds?: Set<RelationKind>) {
  const adjacency = new Map<string, Array<{ neighbor: string; edge: MobEdge }>>();
  for (const edge of edges) {
    if (enabledKinds && !enabledKinds.has(relationKind(edge))) continue;
    const left = adjacency.get(edge.from) ?? [];
    left.push({ neighbor: edge.to, edge });
    adjacency.set(edge.from, left);
    const right = adjacency.get(edge.to) ?? [];
    right.push({ neighbor: edge.from, edge });
    adjacency.set(edge.to, right);
  }
  return adjacency;
}

function neighborhood(nodes: MobNode[], edges: MobEdge[], rootKey: string, depth: number, enabledKinds: Set<RelationKind>, maxNodes: number, forcedKeys: Set<string>): { nodes: LayoutNode[]; edges: MobEdge[]; hops: Map<string, number> } {
  const nodeMap = new Map(nodes.map((node) => [node.key, node]));
  const adjacency = buildAdjacency(edges, enabledKinds);
  const hops = new Map<string, number>();
  if (!rootKey || !nodeMap.has(rootKey)) return { nodes: [], edges: [], hops };
  hops.set(rootKey, 0);
  const queue = [rootKey];
  while (queue.length && hops.size < maxNodes) {
    const key = queue.shift();
    if (!key) break;
    const currentHop = hops.get(key) ?? 0;
    if (currentHop >= depth) continue;
    const neighbors = [...(adjacency.get(key) ?? [])].sort((a, b) => b.edge.evidenceCount - a.edge.evidenceCount);
    for (const item of neighbors) {
      if (hops.has(item.neighbor)) continue;
      hops.set(item.neighbor, currentHop + 1);
      queue.push(item.neighbor);
      if (hops.size >= maxNodes) break;
    }
  }
  for (const forced of forcedKeys) {
    if (nodeMap.has(forced) && !hops.has(forced) && hops.size < maxNodes + forcedKeys.size) hops.set(forced, Math.min(depth, 3));
  }
  const grouped = new Map<number, MobNode[]>();
  for (const [key, hop] of hops) {
    const node = nodeMap.get(key);
    if (!node) continue;
    const rows = grouped.get(hop) ?? [];
    rows.push(node);
    grouped.set(hop, rows);
  }
  const resultNodes: LayoutNode[] = [];
  for (const [hop, rows] of [...grouped.entries()].sort((a, b) => a[0] - b[0])) {
    rows.sort((a, b) => a.zone.localeCompare(b.zone) || b.risk.score - a.risk.score || a.label.localeCompare(b.label));
    if (hop === 0) {
      const node = rows[0];
      if (node) resultNodes.push({ ...node, x: BASE_WIDTH / 2, y: BASE_HEIGHT / 2, width: 190, height: 66, hop });
      continue;
    }
    const radius = hop === 1 ? 205 : hop === 2 ? 345 : 455;
    const width = hop === 1 ? 154 : 136;
    const height = 50;
    rows.forEach((node, index) => {
      const angle = -Math.PI / 2 + (Math.PI * 2 * index) / Math.max(1, rows.length) + hop * 0.13;
      resultNodes.push({ ...node, x: BASE_WIDTH / 2 + Math.cos(angle) * radius, y: BASE_HEIGHT / 2 + Math.sin(angle) * radius, width, height, hop });
    });
  }
  const keys = new Set(resultNodes.map((node) => node.key));
  const resultEdges = edges
    .filter((edge) => keys.has(edge.from) && keys.has(edge.to) && enabledKinds.has(relationKind(edge)))
    .sort((a, b) => b.evidenceCount - a.evidenceCount)
    .slice(0, 900);
  return { nodes: resultNodes, edges: resultEdges, hops };
}

function focusKeys(edges: MobEdge[], rootKey: string, depth: number): Set<string> {
  const result = new Set<string>();
  if (!rootKey) return result;
  const adjacency = buildAdjacency(edges);
  result.add(rootKey);
  const queue: Array<{ key: string; hop: number }> = [{ key: rootKey, hop: 0 }];
  while (queue.length) {
    const current = queue.shift();
    if (!current || current.hop >= depth) continue;
    for (const item of adjacency.get(current.key) ?? []) {
      if (result.has(item.neighbor)) continue;
      result.add(item.neighbor);
      queue.push({ key: item.neighbor, hop: current.hop + 1 });
    }
  }
  return result;
}

function shortestPath(edges: MobEdge[], start: string, target: string, maxDepth = 7): { nodeKeys: string[]; edgeKeys: string[] } | null {
  if (start === target) return { nodeKeys: [start], edgeKeys: [] };
  const adjacency = buildAdjacency(edges);
  const previous = new Map<string, { node: string; edgeKey: string }>();
  const depth = new Map<string, number>([[start, 0]]);
  const queue = [start];
  let found = false;
  while (queue.length && !found) {
    const key = queue.shift();
    if (!key) break;
    const hop = depth.get(key) ?? 0;
    if (hop >= maxDepth) continue;
    const neighbors = [...(adjacency.get(key) ?? [])].sort((a, b) => b.edge.confidence - a.edge.confidence || b.edge.evidenceCount - a.edge.evidenceCount);
    for (const item of neighbors) {
      if (depth.has(item.neighbor)) continue;
      previous.set(item.neighbor, { node: key, edgeKey: item.edge.key });
      depth.set(item.neighbor, hop + 1);
      if (item.neighbor === target) { found = true; break; }
      queue.push(item.neighbor);
    }
  }
  if (!depth.has(target)) return null;
  const nodeKeys = [target];
  const edgeKeys: string[] = [];
  let cursor = target;
  while (cursor !== start) {
    const prev = previous.get(cursor);
    if (!prev) return null;
    edgeKeys.push(prev.edgeKey);
    nodeKeys.push(prev.node);
    cursor = prev.node;
  }
  nodeKeys.reverse();
  edgeKeys.reverse();
  return { nodeKeys, edgeKeys };
}

function crownPaths(nodes: MobNode[], edges: MobEdge[], startKey: string): CrownPath[] {
  const targets = nodes.filter((node) => node.crownJewel && node.key !== startKey).sort((a, b) => b.risk.score - a.risk.score);
  const paths: CrownPath[] = [];
  for (const target of targets.slice(0, 40)) {
    const path = shortestPath(edges, startKey, target.key, 8);
    if (!path) continue;
    paths.push({ targetKey: target.key, targetLabel: target.label, ...path });
  }
  return paths.sort((a, b) => a.edgeKeys.length - b.edgeKeys.length).slice(0, 6);
}

function aiConnectorPaths(edges: MobEdge[], keys: string[]): { nodeKeys: Set<string>; edgeKeys: Set<string>; connected: number } {
  const nodeKeys = new Set<string>(keys);
  const edgeKeys = new Set<string>();
  if (keys.length < 2) return { nodeKeys, edgeKeys, connected: 0 };
  const root = keys[0];
  let connected = 0;
  for (const target of keys.slice(1, 9)) {
    const path = shortestPath(edges, root, target, 7);
    if (!path) continue;
    connected += 1;
    path.nodeKeys.forEach((key) => nodeKeys.add(key));
    path.edgeKeys.forEach((key) => edgeKeys.add(key));
  }
  return { nodeKeys, edgeKeys, connected };
}

function snapshotDiff(current: MobSnapshot, previous: MobSnapshot | null): DiffSummary | null {
  if (!previous) return null;
  const currentNodes = new Map(current.nodes.map((node) => [node.key, node]));
  const previousNodes = new Map(previous.nodes.map((node) => [node.key, node]));
  const currentEdges = new Map(current.edges.map((edge) => [edge.key, edge]));
  const previousEdges = new Map(previous.edges.map((edge) => [edge.key, edge]));
  const result: DiffSummary = { newNodes: new Set(), removedNodes: new Set(), changedNodes: new Set(), newEdges: new Set(), removedEdges: new Set(), changedEdges: new Set() };
  for (const [key, node] of currentNodes) {
    const prior = previousNodes.get(key);
    if (!prior) { result.newNodes.add(key); continue; }
    if (node.risk.score !== prior.risk.score || node.monitoring.status !== prior.monitoring.status || node.monitoring.coveragePercent !== prior.monitoring.coveragePercent || node.threatState !== prior.threatState || node.crownJewel !== prior.crownJewel || node.criticality !== prior.criticality) result.changedNodes.add(key);
  }
  for (const key of previousNodes.keys()) if (!currentNodes.has(key)) result.removedNodes.add(key);
  for (const [key, edge] of currentEdges) {
    const prior = previousEdges.get(key);
    if (!prior) { result.newEdges.add(key); continue; }
    if (edge.evidenceCount !== prior.evidenceCount || edge.confidence !== prior.confidence || edge.provenance !== prior.provenance || edge.lastSeen !== prior.lastSeen) result.changedEdges.add(key);
  }
  for (const key of previousEdges.keys()) if (!currentEdges.has(key)) result.removedEdges.add(key);
  return result;
}

function diffStatus(key: string, result: DiffSummary | null, kind: 'node' | 'edge'): DiffStatus {
  if (!result) return 'unchanged';
  if (kind === 'node') return result.newNodes.has(key) ? 'new' : result.changedNodes.has(key) ? 'changed' : 'unchanged';
  return result.newEdges.has(key) ? 'new' : result.changedEdges.has(key) ? 'changed' : 'unchanged';
}

function matchesQuery(node: MobNode, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  const tokens = trimmed.split(/\s+/).filter(Boolean);
  const blob = `${node.label} ${node.type} ${node.zone} ${node.monitoring.status} ${node.criticality ?? ''} ${JSON.stringify(node.properties)} ${node.sourceTables.join(' ')}`.toLowerCase();
  return tokens.every((token) => {
    const [field, ...rest] = token.split(':');
    if (!rest.length) return blob.includes(token);
    const value = rest.join(':');
    if (field === 'risk') {
      if (value === 'critical') return node.risk.score >= 85;
      if (value === 'high') return node.risk.score >= 65 && node.risk.score < 85;
      if (value === 'medium') return node.risk.score >= 35 && node.risk.score < 65;
      if (value === 'low') return node.risk.score < 35;
      const threshold = Number(value);
      return Number.isFinite(threshold) ? node.risk.score >= threshold : false;
    }
    if (field === 'type') return normalize(node.type).includes(value);
    if (field === 'zone') return normalize(node.zone).includes(value);
    if (field === 'monitoring') return normalize(node.monitoring.status).includes(value);
    if (field === 'crown') return value === 'true' ? node.crownJewel : value === 'false' ? !node.crownJewel : false;
    if (field === 'threat') return normalize(node.threatState).includes(value);
    if (field === 'source') return node.sourceTables.some((source) => normalize(source).includes(value));
    return blob.includes(token);
  });
}

function curvedPath(from: LayoutNode, to: LayoutNode): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = -dy / distance;
  const ny = dx / distance;
  const bend = Math.min(85, Math.max(16, distance * 0.09)) * (from.y <= to.y ? 1 : -1);
  const cx = (from.x + to.x) / 2 + nx * bend;
  const cy = (from.y + to.y) / 2 + ny * bend;
  return `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`;
}

function edgeTone(edge: MobEdge) {
  const kind = relationKind(edge);
  if (kind === 'authentication') return 'rgba(167,139,250,.78)';
  if (kind === 'network') return 'rgba(34,211,238,.78)';
  if (kind === 'process') return 'rgba(52,211,153,.72)';
  if (kind === 'threat') return 'rgba(251,113,133,.84)';
  if (kind === 'architecture') return 'rgba(96,165,250,.48)';
  return 'rgba(148,163,184,.46)';
}

function zoneRiskScore(zoneId: string, nodes: MobNode[]): number {
  const rows = nodes.filter((node) => node.zone === zoneId);
  if (!rows.length) return 0;
  const mean = rows.reduce((sum, node) => sum + node.risk.score, 0) / rows.length;
  const highRatio = rows.filter((node) => node.risk.score >= 65).length / rows.length;
  const threatRatio = rows.filter((node) => node.threatState !== 'normal').length / rows.length;
  return Math.round(Math.min(100, mean * 0.62 + highRatio * 25 + threatRatio * 20));
}

function zoneHeatFill(score: number): string {
  if (score >= 75) return 'rgba(127,29,29,.24)';
  if (score >= 55) return 'rgba(124,45,18,.20)';
  if (score >= 35) return 'rgba(120,53,15,.15)';
  return 'rgba(8,145,178,.06)';
}

function miniZoneFill(zoneId: string): string {
  if (zoneId === 'identity') return '#8b5cf6';
  if (zoneId === 'azure') return '#0ea5e9';
  if (zoneId === 'onprem') return '#22c55e';
  if (zoneId === 'security') return '#06b6d4';
  if (zoneId === 'threat') return '#f43f5e';
  if (zoneId === 'external') return '#f97316';
  return '#64748b';
}

export function MainOperatingBaseExplorer({
  snapshot,
  snapshots,
  customerId,
  customerName,
  aiOverlay,
  stage2d1Enabled = true,
  stage2d2Enabled = true,
  stage2d3cEnabled = true,
  stage2d3c1Enabled = true,
  drilldownRequest = null,
  onOpenReplay,
  onClearAiOverlay,
  onNavigate,
}: {
  snapshot: MobSnapshot;
  snapshots: MobSnapshotSummary[];
  customerId: string;
  customerName: string;
  aiOverlay: AiC2MobOverlay | null;
  stage2d1Enabled?: boolean;
  stage2d2Enabled?: boolean;
  stage2d3cEnabled?: boolean;
  stage2d3c1Enabled?: boolean;
  drilldownRequest?: MobEstateDrilldownRequest | null;
  onOpenReplay?: () => void;
  onClearAiOverlay: () => void;
  onNavigate?: (tabId: string, customerId?: string) => void;
}) {
  const { canCapability } = useAuth();
  const [lens, setLens] = useState<LensId>('architecture');
  const [sentinelScope, setSentinelScope] = useState<MobSentinelScope>('active_incidents');
  const [sentinelSeverity, setSentinelSeverity] = useState<MobSentinelSeverity>('all');
  const [activeDrilldown, setActiveDrilldown] = useState<MobEstateDrilldownRequest | null>(null);
  const [canvasMode, setCanvasMode] = useState<CanvasMode>('architecture');
  const [density, setDensity] = useState<DensityId>('balanced');
  const [immersive, setImmersive] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [focusedZoneId, setFocusedZoneId] = useState('');
  const [focusedPage, setFocusedPage] = useState(0);
  const [selectedNodeKey, setSelectedNodeKey] = useState('');
  const [selectedEdgeKey, setSelectedEdgeKey] = useState('');
  const [focusDepth, setFocusDepth] = useState<0 | 1 | 2 | 3>(0);
  const [graphDepth, setGraphDepth] = useState<1 | 2 | 3>(2);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [heatmap, setHeatmap] = useState(false);
  const [showMinimap, setShowMinimap] = useState(() => {
    try { return sessionStorage.getItem('kloudsecure:mob:minimap-visible') !== 'false'; } catch { return true; }
  });
  const [enabledRelations, setEnabledRelations] = useState<Set<RelationKind>>(new Set(['authentication', 'network', 'process', 'threat', 'architecture', 'other']));
  const [compareChoice, setCompareChoice] = useState('');
  const [compareSnapshot, setCompareSnapshot] = useState<MobSnapshot | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [pathResult, setPathResult] = useState<CrownPath[]>([]);
  const [pathEdgeKeys, setPathEdgeKeys] = useState<Set<string>>(new Set());
  const [pathNodeKeys, setPathNodeKeys] = useState<Set<string>>(new Set());
  const [aiPathEdgeKeys, setAiPathEdgeKeys] = useState<Set<string>>(new Set());
  const [aiPathNodeKeys, setAiPathNodeKeys] = useState<Set<string>>(new Set());
  const graphCardRef = useRef<HTMLElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const panRef = useRef({ active: false, pointerId: -1, clientX: 0, clientY: 0, panX: 0, panY: 0 });

  const canImmersive = canCapability('main_operating_base', 'use_immersive_architecture');
  const canProvenance = canCapability('main_operating_base', 'view_relationship_provenance');
  const canCrown = canCapability('main_operating_base', 'view_crown_jewels');
  const canExport = canCapability('main_operating_base', 'export_architecture');
  const canAiOverlay = canCapability('main_operating_base', 'receive_ai_c2_overlay');
  const canExpand = stage2d1Enabled && canCapability('main_operating_base', 'expand_zones');
  const canIntelGraph = stage2d1Enabled && canCapability('main_operating_base', 'view_intelligence_graph');
  const canFocus = stage2d1Enabled && canCapability('main_operating_base', 'use_entity_focus');
  const canSearch = stage2d1Enabled && canCapability('main_operating_base', 'search_architecture');
  const canMinimap = stage2d1Enabled && canCapability('main_operating_base', 'view_minimap');
  const canDensity = stage2d1Enabled && canCapability('main_operating_base', 'use_density_controls');
  const canInspectEdges = stage2d1Enabled && canCapability('main_operating_base', 'inspect_relationships');
  const canSemanticActivity = stage2d1Enabled && canCapability('main_operating_base', 'view_semantic_activity');
  const canCompare = stage2d1Enabled && canCapability('main_operating_base', 'compare_snapshots');
  const canHeatmap = stage2d1Enabled && canCapability('main_operating_base', 'view_risk_heatmap');
  const canCrownPath = stage2d1Enabled && canCapability('main_operating_base', 'find_crown_jewel_paths');
  const canAiPaths = stage2d1Enabled && canCapability('main_operating_base', 'view_ai_c2_paths');
  const canReplay = stage2d2Enabled && canCapability('main_operating_base', 'view_historical_replay');
  const canProductCoverage = stage2d3cEnabled && canCapability('main_operating_base', 'view_product_coverage');
  const canFreshness = stage2d3cEnabled && canCapability('main_operating_base', 'view_entity_freshness');
  const canSentinelIncidents = stage2d3cEnabled && canCapability('main_operating_base', 'view_sentinel_incidents');
  const canEstateDrilldowns = stage2d3c1Enabled && canCapability('main_operating_base', 'use_estate_intelligence_drilldowns');
  const canSentinelGraphLens = stage2d3c1Enabled && canCapability('main_operating_base', 'view_sentinel_graph_lens');
  const visibleLenses = useMemo(() => LENSES.filter((item) => (item.id !== 'sentinel' || canSentinelGraphLens) && canCapability('main_operating_base', item.capability)), [canCapability, canSentinelGraphLens]);

  useEffect(() => {
    const onFullscreen = () => setIsFullscreen(document.fullscreenElement === graphCardRef.current);
    document.addEventListener('fullscreenchange', onFullscreen);
    return () => document.removeEventListener('fullscreenchange', onFullscreen);
  }, []);

  useEffect(() => {
    setSelectedNodeKey('');
    setSelectedEdgeKey('');
    setFocusedZoneId('');
    setFocusedPage(0);
    setCompareSnapshot(null);
    setCompareChoice('');
    setPathResult([]);
    setPathEdgeKeys(new Set());
    setPathNodeKeys(new Set());
    setAiPathEdgeKeys(new Set());
    setAiPathNodeKeys(new Set());
    setFocusDepth(0);
    setCanvasMode('architecture');
    setActiveDrilldown(null);
    setSentinelScope('active_incidents');
    setSentinelSeverity('all');
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [snapshot.id]);

  useEffect(() => {
    if (!aiOverlay || !canAiOverlay) return;
    setLens('threats');
    if (canImmersive) setImmersive(true);
  }, [aiOverlay?.missionId, canAiOverlay, canImmersive]);

  useEffect(() => {
    if (!drilldownRequest || !canEstateDrilldowns) return;
    setCanvasMode('architecture');
    setSelectedEdgeKey('');
    setFocusedZoneId('');
    setFocusedPage(0);
    setFocusDepth(0);
    setActiveDrilldown(drilldownRequest);
    if (drilldownRequest.kind === 'sentinel') {
      if (canSentinelGraphLens) {
        setLens('sentinel');
        setSentinelScope(drilldownRequest.value === 'alerts' ? 'alerts' : drilldownRequest.value === 'all_incidents' ? 'all_incidents' : 'active_incidents');
        setSentinelSeverity('all');
      } else {
        setLens('threats');
      }
    } else {
      setLens(drilldownRequest.preferredLens === 'monitoring' ? 'monitoring' : 'architecture');
    }
    window.requestAnimationFrame(() => graphCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [drilldownRequest?.id, canEstateDrilldowns, canSentinelGraphLens]);

  useEffect(() => {
    if (!visibleLenses.some((item) => item.id === lens)) setLens(visibleLenses[0]?.id ?? 'architecture');
  }, [visibleLenses, lens]);

  const aiOverlayKeys = useMemo(() => {
    const keys = new Set<string>();
    if (!aiOverlay || aiOverlay.customerId !== snapshot.customerId) return keys;
    for (const entity of aiOverlay.entities) {
      const value = normalize(entity.value);
      if (!value) continue;
      for (const node of snapshot.nodes) {
        if (normalize(node.label) === value || normalize(JSON.stringify(node.properties)).includes(value)) keys.add(node.key);
      }
    }
    return keys;
  }, [snapshot, aiOverlay]);

  const overlayStatusByKey = useMemo(() => {
    const result = new Map<string, string>();
    if (!aiOverlay) return result;
    for (const entity of aiOverlay.entities) {
      const value = normalize(entity.value);
      for (const node of snapshot.nodes) if (normalize(node.label) === value || normalize(JSON.stringify(node.properties)).includes(value)) result.set(node.key, entity.status);
    }
    return result;
  }, [snapshot, aiOverlay]);

  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    return snapshot.nodes
      .filter((node) => matchesQuery(node, search))
      .sort((a, b) => Number(b.crownJewel) - Number(a.crownJewel) || b.risk.score - a.risk.score || a.label.localeCompare(b.label))
      .slice(0, 12);
  }, [snapshot.nodes, search]);

  const sentinelLensResolution = useMemo(
    () => resolveSentinelLensKeys(snapshot.nodes, snapshot.edges, sentinelScope, sentinelSeverity),
    [snapshot.nodes, snapshot.edges, sentinelScope, sentinelSeverity],
  );
  const sentinelSeedCount = useMemo(
    () => countSentinelSeeds(snapshot.nodes, sentinelScope, sentinelSeverity),
    [snapshot.nodes, sentinelScope, sentinelSeverity],
  );
  const drilldownKeys = useMemo(() => {
    if (!activeDrilldown) return new Set<string>();
    if (activeDrilldown.kind === 'sentinel') return new Set(sentinelLensResolution.keys);
    return resolveEstateDrilldownKeys(snapshot.nodes, activeDrilldown).keys;
  }, [snapshot.nodes, activeDrilldown, sentinelLensResolution]);
  const priorityKeys = useMemo(() => {
    const keys = new Set(drilldownKeys);
    if (lens === 'sentinel') sentinelLensResolution.seedKeys.forEach((key) => keys.add(key));
    return keys;
  }, [drilldownKeys, lens, sentinelLensResolution]);
  const drilldownActive = Boolean(activeDrilldown && drilldownKeys.size > 0);

  const pinnedKeys = useMemo(() => {
    const set = new Set<string>();
    if (selectedNodeKey) set.add(selectedNodeKey);
    pathNodeKeys.forEach((key) => set.add(key));
    aiPathNodeKeys.forEach((key) => set.add(key));
    drilldownKeys.forEach((key) => set.add(key));
    return set;
  }, [selectedNodeKey, pathNodeKeys, aiPathNodeKeys, drilldownKeys]);

  const architectureLayout = useMemo(() => layoutArchitecture({
    nodes: snapshot.nodes,
    edges: snapshot.edges,
    zones: snapshot.zones,
    lens,
    density,
    aiKeys: aiOverlayKeys,
    pinnedKeys,
    drilldownKeys: priorityKeys,
    sentinelScope,
    sentinelSeverity,
    focusedZoneId,
    focusedPage,
  }), [snapshot, lens, density, aiOverlayKeys, pinnedKeys, priorityKeys, sentinelScope, sentinelSeverity, focusedZoneId, focusedPage]);

  const forcedGraphKeys = useMemo(() => {
    const set = new Set<string>();
    pathNodeKeys.forEach((key) => set.add(key));
    aiPathNodeKeys.forEach((key) => set.add(key));
    return set;
  }, [pathNodeKeys, aiPathNodeKeys]);

  const intelligence = useMemo(() => neighborhood(
    snapshot.nodes,
    snapshot.edges,
    selectedNodeKey,
    graphDepth,
    enabledRelations,
    density === 'executive' ? 44 : density === 'detailed' ? 150 : 92,
    forcedGraphKeys,
  ), [snapshot.nodes, snapshot.edges, selectedNodeKey, graphDepth, enabledRelations, density, forcedGraphKeys]);

  const layout = canvasMode === 'intelligence'
    ? { nodes: intelligence.nodes, edges: intelligence.edges, zones: [] as LayoutZone[], clipped: Math.max(0, snapshot.nodes.length - intelligence.nodes.length), page: null }
    : architectureLayout;
  const nodeByKey = useMemo(() => new Map(layout.nodes.map((node) => [node.key, node])), [layout.nodes]);
  const selectedNode = snapshot.nodes.find((node) => node.key === selectedNodeKey) ?? null;
  const selectedEdge = snapshot.edges.find((edge) => edge.key === selectedEdgeKey) ?? null;
  const selectedEdges = useMemo(() => snapshot.edges.filter((edge) => edge.from === selectedNodeKey || edge.to === selectedNodeKey), [snapshot.edges, selectedNodeKey]);
  const focusSet = useMemo(() => focusDepth && selectedNodeKey ? focusKeys(snapshot.edges, selectedNodeKey, focusDepth) : new Set<string>(), [snapshot.edges, selectedNodeKey, focusDepth]);
  const diff = useMemo(() => snapshotDiff(snapshot, compareSnapshot), [snapshot, compareSnapshot]);

  useEffect(() => {
    if (!activeDrilldown || !canEstateDrilldowns) return;
    const visibleMatches = architectureLayout.nodes.filter((node) => drilldownKeys.has(node.key));
    if (visibleMatches.length === 1) {
      setSelectedNodeKey(visibleMatches[0]!.key);
      setSelectedEdgeKey('');
    }
    if (visibleMatches.length > 0) {
      const minX = Math.min(...visibleMatches.map((node) => node.x - node.width / 2));
      const maxX = Math.max(...visibleMatches.map((node) => node.x + node.width / 2));
      const minY = Math.min(...visibleMatches.map((node) => node.y - node.height / 2));
      const maxY = Math.max(...visibleMatches.map((node) => node.y + node.height / 2));
      const width = Math.max(180, maxX - minX + 180);
      const height = Math.max(130, maxY - minY + 150);
      const fitZoom = Math.max(0.62, Math.min(1.8, BASE_WIDTH / width, BASE_HEIGHT / height));
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      setZoom(Number(fitZoom.toFixed(2)));
      setPan({ x: BASE_WIDTH / 2 - centerX, y: BASE_HEIGHT / 2 - centerY });
    } else {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
    void auditInteraction('mob_estate_intelligence_drilldown_activated', {
      kind: activeDrilldown.kind,
      value: activeDrilldown.value,
      label: activeDrilldown.label,
      matchedEntities: drilldownKeys.size,
      renderedMatches: visibleMatches.length,
      lens,
    });
  }, [activeDrilldown?.id]);

  const viewWidth = BASE_WIDTH / zoom;
  const viewHeight = BASE_HEIGHT / zoom;
  const viewX = (BASE_WIDTH - viewWidth) / 2 - pan.x;
  const viewY = (BASE_HEIGHT - viewHeight) / 2 - pan.y;

  async function auditInteraction(action: string, details: Record<string, unknown> = {}) {
    try {
      await apiFetch('/main-operating-base/interaction-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, snapshotId: snapshot.id, action, details }),
      });
    } catch {
      // UX telemetry is best-effort. Backend authorization remains authoritative.
    }
  }

  function clearEstateDrilldown() {
    if (!activeDrilldown) return;
    const previous = activeDrilldown;
    setActiveDrilldown(null);
    setSelectedNodeKey('');
    setSelectedEdgeKey('');
    setZoom(1);
    setPan({ x: 0, y: 0 });
    void auditInteraction('mob_estate_intelligence_drilldown_cleared', { kind: previous.kind, value: previous.value, label: previous.label });
  }

  function selectLens(next: LensId) {
    setCanvasMode('architecture');
    setLens(next);
    setSelectedEdgeKey('');
    setFocusedZoneId('');
    setFocusedPage(0);
    if (activeDrilldown) setActiveDrilldown(null);
    if (next === 'sentinel') {
      void auditInteraction('mob_sentinel_lens_viewed', { scope: sentinelScope, severity: sentinelSeverity, seedCount: sentinelSeedCount, relatedEntities: sentinelLensResolution.keys.size });
    }
  }

  function changeSentinelScope(next: MobSentinelScope) {
    setSentinelScope(next);
    setActiveDrilldown(null);
    setSelectedNodeKey('');
    setSelectedEdgeKey('');
    setFocusedZoneId('');
    setFocusedPage(0);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    void auditInteraction('mob_sentinel_lens_scope_changed', { scope: next, severity: sentinelSeverity, seedCount: countSentinelSeeds(snapshot.nodes, next, sentinelSeverity) });
  }

  function changeSentinelSeverity(next: MobSentinelSeverity) {
    setSentinelSeverity(next);
    setActiveDrilldown(null);
    setSelectedNodeKey('');
    setSelectedEdgeKey('');
    setFocusedZoneId('');
    setFocusedPage(0);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    void auditInteraction('mob_sentinel_lens_severity_changed', { scope: sentinelScope, severity: next, seedCount: countSentinelSeeds(snapshot.nodes, sentinelScope, next) });
  }

  useEffect(() => {
    if (!selectedNode || !selectedNode.estate) return;
    if (canProductCoverage) void auditInteraction('mob_product_coverage_viewed', { nodeKey: selectedNode.key, canonicalEntityId: selectedNode.estate.canonicalEntityId, providers: selectedNode.estate.providers });
    if (canFreshness) void auditInteraction('mob_entity_freshness_viewed', { nodeKey: selectedNode.key, canonicalEntityId: selectedNode.estate.canonicalEntityId, freshness: selectedNode.estate.freshness, lastObservedAt: selectedNode.estate.lastObservedAt });
    if (canSentinelIncidents && selectedNode.type === 'incident') void auditInteraction('mob_sentinel_incident_focused', { nodeKey: selectedNode.key, canonicalEntityId: selectedNode.estate.canonicalEntityId, incidentNumber: selectedNode.properties['sentinelIncidentNumber'] ?? (selectedNode.properties['canonicalIdentifiers'] as Record<string, unknown> | undefined)?.['sentinelIncidentNumber'] ?? null, severity: selectedNode.properties['severity'] ?? null, status: selectedNode.properties['status'] ?? null });
  }, [selectedNodeKey, snapshot.id, canProductCoverage, canFreshness, canSentinelIncidents]);

  function resetViewport() { setZoom(1); setPan({ x: 0, y: 0 }); }

  function toggleMinimap() {
    const next = !showMinimap;
    setShowMinimap(next);
    try { sessionStorage.setItem('kloudsecure:mob:minimap-visible', next ? 'true' : 'false'); } catch { /* session preference only */ }
    void auditInteraction('mob_minimap_visibility_changed', { visible: next });
  }

  function expandZone(zoneId: string) {
    if (!canExpand) return;
    setFocusedZoneId(zoneId);
    setFocusedPage(0);
    setSelectedEdgeKey('');
    setCanvasMode('architecture');
    resetViewport();
    const zone = snapshot.zones.find((item) => item.id === zoneId);
    void auditInteraction('mob_zone_expanded', { zoneId, zoneLabel: zone?.label ?? zoneId });
  }

  function collapseZone() {
    setFocusedZoneId('');
    setFocusedPage(0);
    resetViewport();
  }

  function flyToNode(node: MobNode) {
    setSelectedNodeKey(node.key);
    setSelectedEdgeKey('');
    setSearchOpen(false);
    if (canvasMode === 'architecture') {
      if (canExpand) setFocusedZoneId(node.zone);
      setFocusedPage(0);
      setZoom(1.08);
      setPan({ x: 0, y: 0 });
    }
    void auditInteraction('mob_architecture_searched', { query: search, nodeKey: node.key, nodeType: node.type, zone: node.zone });
  }

  function openIntelligence(nodeKey = selectedNodeKey) {
    if (!canIntelGraph || !nodeKey) return;
    setSelectedNodeKey(nodeKey);
    setCanvasMode('intelligence');
    setSelectedEdgeKey('');
    setFocusDepth(0);
    resetViewport();
    void auditInteraction('mob_intelligence_graph_viewed', { rootKey: nodeKey, depth: graphDepth });
  }

  function enableFocus(depth: 1 | 2 | 3) {
    if (!canFocus || !selectedNodeKey) return;
    setFocusDepth(depth);
    void auditInteraction('mob_entity_focused', { nodeKey: selectedNodeKey, depth });
  }

  function inspectEdge(edgeKey: string) {
    if (!canInspectEdges) return;
    setSelectedEdgeKey(edgeKey);
    const edge = snapshot.edges.find((item) => item.key === edgeKey);
    if (edge) void auditInteraction('mob_relationship_inspected', { edgeKey, type: edge.type, provenance: edge.provenance, relationKind: relationKind(edge) });
  }

  async function loadCompare() {
    if (!canCompare || !compareChoice || compareChoice === snapshot.id) return;
    setCompareLoading(true);
    try {
      const data = await apiFetch<{ snapshot?: MobSnapshot | null }>(`/main-operating-base/snapshot/${encodeURIComponent(compareChoice)}?customerId=${encodeURIComponent(customerId)}`);
      if (!data.snapshot) throw new Error('Comparison snapshot could not be loaded.');
      setCompareSnapshot(data.snapshot);
      void auditInteraction('mob_snapshot_compared', { currentSnapshotId: snapshot.id, comparedSnapshotId: data.snapshot.id });
      toast({ title: 'MoB snapshot comparison ready', description: `${formatTime(data.snapshot.generatedAt)} → ${formatTime(snapshot.generatedAt)}` });
    } catch (error) {
      toast({ title: 'Snapshot comparison failed', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
    } finally { setCompareLoading(false); }
  }

  function calculateCrownPaths() {
    if (!canCrownPath || !selectedNodeKey) return;
    const paths = crownPaths(snapshot.nodes, snapshot.edges, selectedNodeKey);
    const edgeSet = new Set<string>();
    const nodeSet = new Set<string>();
    for (const path of paths) { path.edgeKeys.forEach((key) => edgeSet.add(key)); path.nodeKeys.forEach((key) => nodeSet.add(key)); }
    setPathResult(paths);
    setPathEdgeKeys(edgeSet);
    setPathNodeKeys(nodeSet);
    if (paths.length) openIntelligence(selectedNodeKey);
    void auditInteraction('mob_crown_jewel_path_calculated', { nodeKey: selectedNodeKey, pathCount: paths.length, highlightedEdges: edgeSet.size });
    toast({ title: paths.length ? 'Crown-jewel paths illuminated' : 'No reachable crown jewel found', description: paths.length ? `${paths.length} shortest path(s) · ${edgeSet.size} relationship(s) highlighted.` : 'No existing MoB relationship path connected the selected entity to a crown jewel within 8 hops.' });
  }

  function illuminateAiPaths() {
    if (!canAiPaths || aiOverlayKeys.size < 2) return;
    const result = aiConnectorPaths(snapshot.edges, [...aiOverlayKeys]);
    setAiPathEdgeKeys(result.edgeKeys);
    setAiPathNodeKeys(result.nodeKeys);
    if (result.edgeKeys.size) {
      const root = [...aiOverlayKeys][0];
      if (root) { setSelectedNodeKey(root); setCanvasMode('intelligence'); setGraphDepth(3); resetViewport(); }
    }
    void auditInteraction('mob_ai_c2_path_illuminated', { caseRef: aiOverlay?.caseRef ?? null, caseNodes: aiOverlayKeys.size, connectedTargets: result.connected, highlightedEdges: result.edgeKeys.size });
    toast({ title: result.edgeKeys.size ? 'AI C2 case paths illuminated' : 'No connecting MoB paths found', description: result.edgeKeys.size ? `${result.connected} case entity path(s) found across ${result.edgeKeys.size} persisted relationship(s).` : 'MoB did not fabricate a relationship: the persisted graph contains no path between the matched AI C2 entities.' });
  }

  function clearPaths() {
    setPathResult([]); setPathEdgeKeys(new Set()); setPathNodeKeys(new Set()); setAiPathEdgeKeys(new Set()); setAiPathNodeKeys(new Set());
  }

  async function downloadServerExport(format: 'md' | 'txt' | 'json') {
    if (!canExport) return;
    try {
      const response = await fetch(`${getApiBase()}/main-operating-base/export?customerId=${encodeURIComponent(customerId)}&snapshotId=${encodeURIComponent(snapshot.id)}&format=${format}`, { credentials: 'include' });
      if (!response.ok) throw new Error(await response.text() || `Export failed with HTTP ${response.status}`);
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') ?? '';
      const filename = disposition.match(/filename="([^"]+)"/i)?.[1] ?? `KloudSecure-MoB.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast({ title: 'MoB exported', description: filename });
    } catch (error) { toast({ title: 'Export failed', description: error instanceof Error ? error.message : String(error), variant: 'destructive' }); }
  }

  function downloadSvg() {
    if (!svgRef.current || !canExport) return;
    const serialized = new XMLSerializer().serializeToString(svgRef.current);
    const body = serialized.includes('xmlns=') ? serialized : serialized.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
    const blob = new Blob([body], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `KloudSecure-MoB-${customerName.replace(/[^A-Za-z0-9_-]+/g, '-')}-${snapshot.id.slice(0, 8)}-${canvasMode}.svg`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    void apiFetch('/main-operating-base/export-audit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, snapshotId: snapshot.id, format: 'svg', lens: `${canvasMode}:${lens}` }) }).catch(() => undefined);
    toast({ title: 'MoB SVG downloaded', description: `Current ${canvasMode === 'intelligence' ? 'Intelligence Graph' : 'architecture'} view exported.` });
  }

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement === graphCardRef.current) await document.exitFullscreen();
      else await graphCardRef.current?.requestFullscreen();
      setIsFullscreen(document.fullscreenElement !== null);
    } catch (error) {
      toast({ title: 'Fullscreen unavailable', description: error instanceof Error ? error.message : String(error), variant: 'destructive' });
    }
  }

  function onPointerDown(event: ReactPointerEvent<SVGSVGElement>) {
    const target = event.target as Element | null;
    if (target?.closest('[data-mob-node="true"]') || target?.closest('[data-mob-edge="true"]') || target?.closest('[data-mob-zone="true"]')) return;
    panRef.current = { active: true, pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY, panX: pan.x, panY: pan.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: ReactPointerEvent<SVGSVGElement>) {
    const state = panRef.current;
    if (!state.active || state.pointerId !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setPan({ x: state.panX + (event.clientX - state.clientX) * (viewWidth / Math.max(1, rect.width)), y: state.panY + (event.clientY - state.clientY) * (viewHeight / Math.max(1, rect.height)) });
  }

  function onPointerUp(event: ReactPointerEvent<SVGSVGElement>) {
    if (panRef.current.pointerId !== event.pointerId) return;
    panRef.current.active = false;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* noop */ }
  }

  function onWheel(event: ReactWheelEvent<SVGSVGElement>) {
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    setZoom((value) => Math.max(0.5, Math.min(3, Number((value + delta).toFixed(2)))));
  }

  function minimapClick(event: ReactPointerEvent<SVGSVGElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / Math.max(1, rect.width)) * BASE_WIDTH;
    const y = ((event.clientY - rect.top) / Math.max(1, rect.height)) * BASE_HEIGHT;
    setPan({ x: BASE_WIDTH / 2 - x, y: BASE_HEIGHT / 2 - y });
  }

  const selectedZone = focusedZoneId ? snapshot.zones.find((item) => item.id === focusedZoneId) ?? null : null;
  const highlightedEdges = useMemo(() => new Set<string>([...pathEdgeKeys, ...aiPathEdgeKeys]), [pathEdgeKeys, aiPathEdgeKeys]);
  const highlightedNodes = useMemo(() => new Set<string>([...pathNodeKeys, ...aiPathNodeKeys]), [pathNodeKeys, aiPathNodeKeys]);

  return (
    <section ref={graphCardRef} className={`overflow-hidden border border-violet-500/20 bg-slate-950 ${isFullscreen ? 'fixed inset-0 z-[100] flex flex-col rounded-none' : 'rounded-2xl shadow-[0_0_50px_rgba(14,165,233,.06)]'}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 bg-gradient-to-r from-slate-950 via-slate-950 to-violet-950/30 px-4 py-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-black text-slate-100"><Network size={16} className="text-cyan-300" />{stage2d3cEnabled && snapshot.sourceEstateKnowledgeGraphRunId ? stage2d3c1Enabled ? 'MoB Estate Intelligence · Stage 2D.3-C.1' : 'MoB Estate Intelligence · Stage 2D.3-C' : stage2d2Enabled ? 'MoB Deep Exploration · Stage 2D.2 Ready' : stage2d1Enabled ? 'MoB Deep Exploration · Stage 2D.1' : 'MoB Live Architecture · Stage 2D'}</div>
          <div className="mt-1 text-[10px] text-slate-600">MoB {snapshot.id.slice(0, 8)} · {compact(snapshot.nodes.length)} entities · {compact(snapshot.edges.length)} persisted relationships · no live customer query</div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {canImmersive && <div className="mr-1 flex rounded-lg border border-slate-700 bg-slate-900 p-0.5"><button type="button" onClick={() => setImmersive(false)} className={`rounded-md px-2 py-1 text-[9px] font-bold ${!immersive ? 'bg-slate-700 text-white' : 'text-slate-500'}`}>Standard</button><button type="button" onClick={() => setImmersive(true)} className={`rounded-md px-2 py-1 text-[9px] font-bold ${immersive ? 'bg-violet-500 text-white' : 'text-slate-500'}`}>Immersive</button></div>}
          <button type="button" title="Zoom out" onClick={() => setZoom((value) => Math.max(0.5, Number((value - .15).toFixed(2))))} className="rounded-lg border border-slate-700 p-2 text-slate-400 hover:bg-slate-800"><ZoomOut size={14} /></button>
          <button type="button" title="Zoom in" onClick={() => setZoom((value) => Math.min(3, Number((value + .15).toFixed(2))))} className="rounded-lg border border-slate-700 p-2 text-slate-400 hover:bg-slate-800"><ZoomIn size={14} /></button>
          <button type="button" title="Reset view" onClick={resetViewport} className="rounded-lg border border-slate-700 p-2 text-slate-400 hover:bg-slate-800"><RotateCcw size={14} /></button>
          {canMinimap && <button type="button" title={showMinimap ? 'Hide minimap' : 'Show minimap'} onClick={toggleMinimap} className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-2 text-[9px] font-bold ${showMinimap ? 'border-cyan-500/25 bg-cyan-500/8 text-cyan-200' : 'border-slate-700 text-slate-500'}`}><MapIcon size={13} />{showMinimap ? 'Hide map' : 'Show map'}</button>}
          {canReplay && onOpenReplay && <button type="button" title="Historical Architecture & Attack Replay" onClick={onOpenReplay} className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-2 text-[9px] font-black text-fuchsia-100 hover:bg-fuchsia-500/18"><History size={13} />Replay</button>}
          {canExport && <><button type="button" onClick={() => void downloadServerExport('md')} className="rounded-lg border border-cyan-500/25 bg-cyan-500/8 px-2.5 py-2 text-[9px] font-bold text-cyan-200 hover:bg-cyan-500/15"><Download size={13} className="mr-1 inline" />MD</button><button type="button" onClick={() => void downloadServerExport('txt')} className="rounded-lg border border-cyan-500/20 px-2.5 py-2 text-[9px] font-bold text-cyan-300 hover:bg-cyan-500/10">TXT</button><button type="button" onClick={() => void downloadServerExport('json')} className="rounded-lg border border-slate-700 px-2.5 py-2 text-[9px] font-bold text-slate-300 hover:bg-slate-800">JSON</button><button type="button" onClick={downloadSvg} className="rounded-lg border border-violet-500/25 bg-violet-500/8 px-2.5 py-2 text-[9px] font-bold text-violet-200 hover:bg-violet-500/15">SVG</button></>}
          <button type="button" onClick={() => void toggleFullscreen()} className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-[10px] font-black text-cyan-100 hover:bg-cyan-500/20">{isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}{isFullscreen ? 'Exit' : 'Maximize'}</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 bg-slate-900/45 px-4 py-2.5">
        <div className="flex rounded-lg border border-slate-700 bg-slate-950 p-0.5">
          <button type="button" onClick={() => { setCanvasMode('architecture'); resetViewport(); }} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[9px] font-black ${canvasMode === 'architecture' ? 'bg-cyan-500 text-slate-950' : 'text-slate-500'}`}><Layers3 size={12} />Architecture</button>
          {canIntelGraph && <button type="button" disabled={!selectedNodeKey} onClick={() => openIntelligence()} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[9px] font-black disabled:opacity-30 ${canvasMode === 'intelligence' ? 'bg-violet-500 text-white' : 'text-slate-500'}`}><GitBranch size={12} />Intelligence Graph</button>}
        </div>
        {canvasMode === 'architecture' && visibleLenses.map((item) => <button key={item.id} title={item.description} type="button" onClick={() => selectLens(item.id)} className={`rounded-full border px-3 py-1.5 text-[9px] font-bold ${lens === item.id ? 'border-cyan-400/40 bg-cyan-500/15 text-cyan-100' : 'border-slate-700 text-slate-500 hover:text-slate-200'}`}>{item.label}</button>)}
        {canHeatmap && canvasMode === 'architecture' && <button type="button" onClick={() => { setHeatmap((value) => !value); void auditInteraction('mob_risk_heatmap_viewed', { enabled: !heatmap }); }} className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[9px] font-bold ${heatmap ? 'border-rose-400/35 bg-rose-500/15 text-rose-100' : 'border-slate-700 text-slate-500'}`}><Flame size={11} />Heatmap</button>}
        {canvasMode === 'intelligence' && <div className="flex items-center gap-1"><span className="text-[9px] font-bold uppercase text-slate-600">Depth</span>{([1, 2, 3] as const).map((value) => <button key={value} type="button" onClick={() => { setGraphDepth(value); void auditInteraction('mob_intelligence_graph_viewed', { rootKey: selectedNodeKey, depth: value }); }} className={`rounded border px-2 py-1 text-[9px] font-bold ${graphDepth === value ? 'border-violet-400/40 bg-violet-500/15 text-violet-100' : 'border-slate-700 text-slate-500'}`}>{value} hop</button>)}</div>}
        {canDensity && <div className="ml-auto flex rounded-lg border border-slate-700 bg-slate-950 p-0.5">{(['executive', 'balanced', 'detailed'] as const).map((value) => <button key={value} type="button" onClick={() => { setDensity(value); setFocusedPage(0); void auditInteraction('mob_density_changed', { density: value }); }} className={`rounded-md px-2 py-1 text-[8px] font-black uppercase ${density === value ? 'bg-slate-700 text-white' : 'text-slate-600'}`}>{value}</button>)}</div>}
      </div>

      {canvasMode === 'architecture' && ((lens === 'sentinel' && canSentinelGraphLens) || activeDrilldown) && <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 bg-slate-950/70 px-4 py-2.5">
        {lens === 'sentinel' && canSentinelGraphLens && <>
          <div className="flex items-center gap-1"><span className="mr-1 text-[8px] font-black uppercase tracking-wider text-rose-300">Sentinel scope</span>{([
            ['active_incidents', 'Active incidents'],
            ['all_incidents', 'All incidents'],
            ['alerts', 'Alerts'],
          ] as const).map(([value, label]) => <button key={value} type="button" onClick={() => changeSentinelScope(value)} className={`rounded-full border px-2.5 py-1 text-[8px] font-bold ${sentinelScope === value ? 'border-rose-400/40 bg-rose-500/15 text-rose-100' : 'border-slate-700 text-slate-500 hover:text-slate-200'}`}>{label}</button>)}</div>
          <div className="ml-2 flex items-center gap-1"><span className="mr-1 text-[8px] font-black uppercase tracking-wider text-slate-600">Severity</span>{(['all', 'critical', 'high', 'medium', 'low'] as const).map((value) => <button key={value} type="button" onClick={() => changeSentinelSeverity(value)} className={`rounded border px-2 py-1 text-[8px] font-bold uppercase ${sentinelSeverity === value ? 'border-amber-400/35 bg-amber-500/10 text-amber-100' : 'border-slate-700 text-slate-600 hover:text-slate-300'}`}>{value}</button>)}</div>
          <div className="ml-auto text-[8px] text-slate-500"><span className="font-black text-rose-200">{sentinelSeedCount}</span> seed {sentinelScope === 'alerts' ? 'alerts' : 'incidents'} · <span className="font-black text-cyan-200">{sentinelLensResolution.keys.size}</span> entities in 2-hop persisted context</div>
        </>}
        {activeDrilldown && <div className={`${lens === 'sentinel' ? 'w-full border-t border-slate-800 pt-2' : 'w-full'} flex flex-wrap items-center gap-2`}><span className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-[9px] font-black text-cyan-100">Estate drilldown · {activeDrilldown.label}</span><span className="text-[9px] text-slate-500">{drilldownKeys.size} matching persisted entities · matches prioritized and unrelated nodes dimmed</span><button type="button" onClick={clearEstateDrilldown} className="rounded border border-slate-700 px-2 py-1 text-[8px] font-bold text-slate-400 hover:bg-slate-800">Clear</button></div>}
      </div>}

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-800 bg-slate-950/80 px-4 py-2.5">
        {canSearch && <div className="relative min-w-[280px] flex-1 max-w-xl">
          <Search size={13} className="pointer-events-none absolute left-3 top-2.5 text-slate-600" />
          <input value={search} onFocus={() => setSearchOpen(true)} onChange={(event) => { setSearch(event.target.value); setSearchOpen(true); }} placeholder="Search or use risk:critical type:endpoint monitoring:blind crown:true…" className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2 pl-9 pr-8 text-[10px] text-slate-200 outline-none focus:border-cyan-500/50" />
          {search && <button type="button" onClick={() => { setSearch(''); setSearchOpen(false); }} className="absolute right-2 top-2 p-1 text-slate-600 hover:text-slate-300"><X size={12} /></button>}
          {searchOpen && search.trim() && <div className="absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-xl border border-slate-700 bg-slate-950 p-1 shadow-2xl">
            {searchResults.map((node) => <button key={node.key} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => flyToNode(node)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-slate-900"><span className={`w-8 text-right text-[9px] font-black ${riskClass(node.risk.score)}`}>R{node.risk.score}</span><span className="min-w-0 flex-1"><span className="block truncate text-[10px] font-bold text-slate-200">{node.label}</span><span className="block truncate text-[8px] uppercase text-slate-600">{node.type.replaceAll('_',' ')} · {node.zone} · {node.monitoring.status}</span></span>{node.crownJewel && <ShieldCheck size={12} className="text-yellow-300" />}</button>)}
            {!searchResults.length && <div className="px-3 py-4 text-center text-[10px] text-slate-600">No architecture entity matched this query.</div>}
          </div>}
        </div>}

        {canCompare && snapshots.length > 1 && <div className="flex items-center gap-1.5"><GitCompareArrows size={13} className="text-violet-300" /><select value={compareChoice} onChange={(event) => setCompareChoice(event.target.value)} className="max-w-[235px] rounded-lg border border-slate-700 bg-slate-900 px-2 py-2 text-[9px] text-slate-300"><option value="">Compare snapshot…</option>{snapshots.filter((item) => item.id !== snapshot.id).map((item) => <option key={item.id} value={item.id}>{formatTime(item.generatedAt)} · {n(item.summary.nodes)} entities</option>)}</select><button type="button" disabled={!compareChoice || compareLoading} onClick={() => void loadCompare()} className="rounded-lg border border-violet-500/25 px-2.5 py-2 text-[9px] font-bold text-violet-200 disabled:opacity-30">{compareLoading ? 'Loading…' : 'Compare'}</button>{compareSnapshot && <button type="button" onClick={() => { setCompareSnapshot(null); setCompareChoice(''); }} className="rounded-lg border border-slate-700 p-2 text-slate-500"><X size={11} /></button>}</div>}
      </div>

      {focusedZoneId && canvasMode === 'architecture' && <div className="flex flex-wrap items-center gap-2 border-b border-cyan-500/20 bg-cyan-950/20 px-4 py-2 text-[10px]"><button type="button" onClick={collapseZone} className="inline-flex items-center gap-1 font-bold text-cyan-300 hover:text-cyan-100"><ChevronLeft size={12} />MoB</button><span className="text-slate-700">/</span><span className="font-black uppercase tracking-wider text-slate-200">{selectedZone?.label ?? focusedZoneId}</span>{layout.page && <span className="text-slate-500">Showing {layout.page.start}-{layout.page.end} of {layout.page.total}</span>}{layout.page && layout.page.pages > 1 && <div className="ml-auto flex items-center gap-1"><button type="button" disabled={layout.page.page <= 0} onClick={() => setFocusedPage((value) => Math.max(0, value - 1))} className="rounded border border-slate-700 p-1.5 text-slate-400 disabled:opacity-30"><ChevronLeft size={11} /></button><span className="px-2 text-[9px] text-slate-500">Page {layout.page.page + 1}/{layout.page.pages}</span><button type="button" disabled={layout.page.page >= layout.page.pages - 1} onClick={() => setFocusedPage((value) => Math.min(layout.page!.pages - 1, value + 1))} className="rounded border border-slate-700 p-1.5 text-slate-400 disabled:opacity-30"><ChevronRight size={11} /></button></div>}</div>}

      {canvasMode === 'intelligence' && <div className="flex flex-wrap gap-1.5 border-b border-slate-800 bg-violet-950/15 px-4 py-2">{(Object.keys(RELATION_LABELS) as RelationKind[]).map((kind) => <button key={kind} type="button" onClick={() => setEnabledRelations((current) => { const next = new Set(current); if (next.has(kind)) next.delete(kind); else next.add(kind); return next; })} className={`rounded-full border px-2.5 py-1 text-[8px] font-bold ${enabledRelations.has(kind) ? 'border-violet-400/30 bg-violet-500/10 text-violet-100' : 'border-slate-800 text-slate-700'}`}>{RELATION_LABELS[kind]}</button>)}</div>}

      {aiOverlay && canAiOverlay && <div className="flex flex-wrap items-center gap-2 border-b border-fuchsia-500/20 bg-fuchsia-950/25 px-4 py-2 text-[10px]"><span className="font-black uppercase tracking-wider text-fuchsia-200"><Radar size={12} className="mr-1 inline" />AI C2 case illumination</span><span className="font-bold text-fuchsia-100">{aiOverlay.caseRef}</span><span className="text-slate-500">{aiOverlayKeys.size} MoB node(s) matched from {aiOverlay.entities.length} case entities</span>{aiOverlay.sourceFobSnapshotId && aiOverlay.sourceFobSnapshotId !== snapshot.sourceFobSnapshotId && <span className="rounded border border-amber-400/25 bg-amber-500/10 px-2 py-0.5 text-amber-200">case FOB differs from this MoB source FOB</span>}{canAiPaths && aiOverlayKeys.size > 1 && <button type="button" onClick={illuminateAiPaths} className="rounded border border-fuchsia-400/25 bg-fuchsia-500/10 px-2 py-1 font-bold text-fuchsia-100"><Route size={11} className="mr-1 inline" />Connect case entities</button>}<button type="button" onClick={() => { setAiPathEdgeKeys(new Set()); setAiPathNodeKeys(new Set()); onClearAiOverlay(); }} className="ml-auto rounded border border-fuchsia-400/20 px-2 py-1 font-bold text-fuchsia-200 hover:bg-fuchsia-500/10">Clear</button></div>}

      {diff && compareSnapshot && <div className="flex flex-wrap items-center gap-3 border-b border-emerald-500/15 bg-emerald-950/15 px-4 py-2 text-[9px]"><span className="font-black uppercase tracking-wider text-emerald-200"><GitCompareArrows size={11} className="mr-1 inline" />Architecture diff</span><span className="text-slate-500">{formatTime(compareSnapshot.generatedAt)} → {formatTime(snapshot.generatedAt)}</span><span className="text-emerald-300">+{diff.newNodes.size} entities</span><span className="text-amber-300">∆{diff.changedNodes.size} changed</span><span className="text-rose-300">−{diff.removedNodes.size} removed</span><span className="text-cyan-300">+{diff.newEdges.size} paths</span><span className="text-slate-400">−{diff.removedEdges.size} paths</span></div>}

      {(pathResult.length > 0 || highlightedEdges.size > 0) && <div className="flex flex-wrap items-center gap-2 border-b border-yellow-500/15 bg-yellow-950/15 px-4 py-2 text-[9px]"><span className="font-black uppercase text-yellow-200"><Route size={11} className="mr-1 inline" />Path illumination</span>{pathResult.slice(0, 4).map((path) => <span key={path.targetKey} className="rounded border border-yellow-500/20 bg-yellow-500/5 px-2 py-1 text-yellow-100">{path.edgeKeys.length} hops → {path.targetLabel}</span>)}<span className="text-slate-500">{highlightedEdges.size} relationship(s) highlighted</span><button type="button" onClick={clearPaths} className="ml-auto rounded border border-slate-700 px-2 py-1 text-slate-500">Clear paths</button></div>}

      <div className={`grid min-h-0 ${isFullscreen ? 'flex-1 grid-cols-[minmax(0,1fr)_350px]' : 'xl:grid-cols-[minmax(0,1fr)_340px]'}`}>
        <div className={`relative overflow-hidden bg-slate-950 ${isFullscreen ? 'min-h-0' : 'min-h-[720px]'}`}>
          <svg
            ref={svgRef}
            className={`h-full w-full select-none ${isFullscreen ? 'min-h-0' : 'min-h-[720px]'}`}
            viewBox={`${viewX} ${viewY} ${viewWidth} ${viewHeight}`}
            preserveAspectRatio="xMidYMid meet"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
            onClick={() => { setSearchOpen(false); }}
            style={{ cursor: panRef.current.active ? 'grabbing' : 'grab', touchAction: 'none' }}
          >
            <defs>
              <radialGradient id="mob21Bg" cx="50%" cy="42%" r="70%"><stop offset="0%" stopColor="rgb(12,27,52)" /><stop offset="50%" stopColor="rgb(4,13,30)" /><stop offset="100%" stopColor="rgb(2,6,23)" /></radialGradient>
              <radialGradient id="mob21Halo" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="rgba(6,182,212,.10)" /><stop offset="65%" stopColor="rgba(139,92,246,.035)" /><stop offset="100%" stopColor="rgba(2,6,23,0)" /></radialGradient>
              <pattern id="mob21Grid" width="34" height="34" patternUnits="userSpaceOnUse"><path d="M 34 0 L 0 0 0 34" fill="none" stroke="rgba(71,85,105,.18)" strokeWidth="0.7" /></pattern>
              <filter id="mob21Glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="4" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
              <filter id="mob21GlowStrong" x="-120%" y="-120%" width="340%" height="340%"><feGaussianBlur stdDeviation="7" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
              <marker id="mob21Arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3 z" fill="rgba(34,211,238,.7)" /></marker>
              <marker id="mob21ArrowDeclared" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L0,6 L7,3 z" fill="rgba(148,163,184,.5)" /></marker>
            </defs>
            <rect x="-900" y="-900" width={BASE_WIDTH + 1800} height={BASE_HEIGHT + 1800} fill={immersive ? 'url(#mob21Bg)' : 'rgb(2,6,23)'} />
            {immersive && <><ellipse cx={BASE_WIDTH * .54} cy={BASE_HEIGHT * .45} rx={BASE_WIDTH * .53} ry={BASE_HEIGHT * .64} fill="url(#mob21Halo)" /><rect x="-500" y="-500" width={BASE_WIDTH + 1000} height={BASE_HEIGHT + 1000} fill="url(#mob21Grid)" />{STARS.map((star, index) => <circle key={index} cx={star.x} cy={star.y} r={star.r} fill="white" opacity={star.opacity} />)}</>}

            {canvasMode === 'intelligence' && <g pointerEvents="none"><circle cx={BASE_WIDTH/2} cy={BASE_HEIGHT/2} r="205" fill="none" stroke="rgba(139,92,246,.13)" strokeDasharray="5 9" /><circle cx={BASE_WIDTH/2} cy={BASE_HEIGHT/2} r="345" fill="none" stroke="rgba(34,211,238,.09)" strokeDasharray="5 11" /><circle cx={BASE_WIDTH/2} cy={BASE_HEIGHT/2} r="455" fill="none" stroke="rgba(148,163,184,.07)" strokeDasharray="4 13" /><text x={BASE_WIDTH/2 + 10} y={BASE_HEIGHT/2 - 216} fill="rgba(196,181,253,.5)" fontSize="9" fontWeight="800">1 HOP</text><text x={BASE_WIDTH/2 + 10} y={BASE_HEIGHT/2 - 356} fill="rgba(103,232,249,.35)" fontSize="9" fontWeight="800">2 HOPS</text>{graphDepth === 3 && <text x={BASE_WIDTH/2 + 10} y={BASE_HEIGHT/2 - 466} fill="rgba(148,163,184,.3)" fontSize="9" fontWeight="800">3 HOPS</text>}</g>}

            {canvasMode === 'architecture' && layout.zones.map((zone) => {
              const score = heatmap ? zoneRiskScore(zone.id, snapshot.nodes) : 0;
              const active = selectedNode?.zone === zone.id || focusedZoneId === zone.id;
              return <g key={zone.id} data-mob-zone="true" onPointerDown={(event) => event.stopPropagation()} onDoubleClick={() => expandZone(zone.id)} onClick={() => { if (canExpand && zone.clipped > 0) expandZone(zone.id); }} style={{ cursor: canExpand && zone.clipped > 0 ? 'zoom-in' : 'default' }}>
                <rect x={zone.x} y={zone.y} width={zone.width} height={zone.height} rx="22" fill={heatmap ? zoneHeatFill(score) : active ? 'rgba(8,145,178,.07)' : 'rgba(15,23,42,.38)'} stroke={active ? 'rgba(34,211,238,.58)' : heatmap && score >= 65 ? 'rgba(251,113,133,.42)' : 'rgba(139,92,246,.20)'} strokeWidth={active ? 1.9 : 1} strokeDasharray={immersive ? '4 7' : undefined} />
                <text x={zone.x + 18} y={zone.y + 25} fill="rgb(226,232,240)" fontSize="12" fontWeight="900">{zone.label.toUpperCase()}</text>
                <text x={zone.x + 18} y={zone.y + 43} fill="rgb(100,116,139)" fontSize="8.5" fontWeight="600">{zone.nodeCount} entities · {zone.coveragePercent}% coverage · {zone.highRiskCount} high-risk{heatmap ? ` · heat ${score}` : ''}</text>
                {zone.clipped > 0 && <g><rect x={zone.x + zone.width - 145} y={zone.y + 10} width="130" height="24" rx="9" fill="rgba(15,23,42,.78)" stroke="rgba(34,211,238,.16)" /><text x={zone.x + zone.width - 23} y={zone.y + 26} textAnchor="end" fill="rgb(103,232,249)" fontSize="8" fontWeight="900">+{zone.clipped} summarized{stage2d1Enabled && canExpand ? ' · EXPAND' : ''}</text></g>}
              </g>;
            })}

            {layout.edges.map((edge) => {
              const from = nodeByKey.get(edge.from); const to = nodeByKey.get(edge.to); if (!from || !to) return null;
              const selected = selectedEdgeKey === edge.key || Boolean(selectedNodeKey && (edge.from === selectedNodeKey || edge.to === selectedNodeKey));
              const aiDirect = aiOverlayKeys.has(edge.from) && aiOverlayKeys.has(edge.to);
              const aiPath = aiPathEdgeKeys.has(edge.key);
              const crownPath = pathEdgeKeys.has(edge.key);
              const highlighted = highlightedEdges.has(edge.key);
              const diffState = diffStatus(edge.key, diff, 'edge');
              const activity = activityState(edge, snapshot.generatedAt);
              const baseWidth = 1.1 + Math.min(2.3, Math.log2(Math.max(1, edge.evidenceCount)) * .42);
              const width = highlighted ? 4.2 : selected ? baseWidth + 1.6 : diffState !== 'unchanged' ? baseWidth + .7 : baseWidth;
              const stroke = aiPath ? 'rgba(244,114,182,.98)' : crownPath ? 'rgba(250,204,21,.95)' : diffState === 'new' ? 'rgba(52,211,153,.92)' : diffState === 'changed' ? 'rgba(251,191,36,.90)' : edgeTone(edge);
              const dash = edge.provenance === 'declared' ? '2 7' : edge.provenance === 'derived' ? '7 6' : undefined;
              const path = curvedPath(from, to);
              const semanticAnimate = stage2d1Enabled && canSemanticActivity && (aiDirect || highlighted || activity === 'live' || (activity === 'recent' && edge.provenance === 'observed'));
              const baselineAnimate = !stage2d1Enabled && (aiDirect || selected || (edge.provenance === 'observed' && edge.evidenceCount >= 3));
              const animate = immersive && (semanticAnimate || baselineAnimate);
              const duration = aiDirect || aiPath ? '1.1s' : crownPath ? '1.45s' : activity === 'live' ? '1.8s' : '4s';
              const drilldownRelated = !drilldownActive || drilldownKeys.has(edge.from) || drilldownKeys.has(edge.to);
              return <g key={edge.key} data-mob-edge="true" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); inspectEdge(edge.key); }} style={{ cursor: canInspectEdges ? 'pointer' : 'default' }} opacity={drilldownRelated ? 1 : 0.08}>
                {immersive && (highlighted || activity !== 'historical') && <path d={path} fill="none" stroke={aiPath ? 'rgba(244,114,182,.14)' : crownPath ? 'rgba(250,204,21,.13)' : 'rgba(34,211,238,.065)'} strokeWidth={width + 7} strokeLinecap="round" pointerEvents="none" />}
                <path d={path} fill="none" stroke={stroke} strokeWidth={width} strokeDasharray={dash} strokeLinecap="round" markerEnd={edge.provenance === 'declared' ? 'url(#mob21ArrowDeclared)' : 'url(#mob21Arrow)'} pointerEvents="none" />
                {animate && <path d={path} fill="none" stroke={aiPath ? 'rgba(244,114,182,.98)' : crownPath ? 'rgba(253,224,71,.95)' : activity === 'live' ? 'rgba(255,255,255,.72)' : 'rgba(103,232,249,.44)'} strokeWidth={Math.max(1.2, width * .62)} strokeDasharray="2 14" strokeLinecap="round" pointerEvents="none"><animate attributeName="stroke-dashoffset" values="0;-72" dur={duration} repeatCount="indefinite" /></path>}
                {canInspectEdges && <path d={path} fill="none" stroke="rgba(255,255,255,0.001)" strokeWidth="14" strokeLinecap="round"><title>{edge.label} · {edge.provenance} · {edge.evidenceCount} evidence · {activity}</title></path>}
              </g>;
            })}

            {layout.nodes.map((node) => {
              const selected = node.key === selectedNodeKey;
              const ai = aiOverlayKeys.has(node.key);
              const onPath = highlightedNodes.has(node.key);
              const aiStatus = overlayStatusByKey.get(node.key) ?? 'investigating';
              const monitoring = node.monitoring.status;
              const relationFocus = focusDepth === 0 || focusSet.has(node.key);
              const status = diffStatus(node.key, diff, 'node');
              const baseStroke = status === 'new' ? 'rgba(52,211,153,.98)' : status === 'changed' ? 'rgba(251,191,36,.98)' : node.crownJewel && canCrown ? 'rgba(250,204,21,.94)' : node.risk.score >= 85 ? 'rgba(251,113,133,.94)' : node.risk.score >= 65 ? 'rgba(251,146,60,.86)' : monitoring === 'blind' ? 'rgba(244,63,94,.72)' : monitoring === 'partial' ? 'rgba(245,158,11,.64)' : 'rgba(56,189,248,.32)';
              const fill = selected ? 'rgba(8,145,178,.40)' : node.threatState === 'active_threat' ? 'rgba(76,5,25,.64)' : node.risk.score >= 65 ? 'rgba(67,31,15,.55)' : 'rgba(10,20,40,.94)';
              const tooltip = `${node.label}\n${node.type.replaceAll('_',' ')} · ${node.zone}\nRisk ${node.risk.score} (${node.risk.level})\nMonitoring ${monitoring} ${node.monitoring.coveragePercent}%${node.criticality ? `\nBusiness criticality ${node.criticality}` : ''}\nSources: ${node.sourceTables.join(', ') || 'none'}${status !== 'unchanged' ? `\nSnapshot diff: ${status}` : ''}`;
              return <g key={node.key} data-mob-node="true" transform={`translate(${node.x - node.width/2} ${node.y - node.height/2})`} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); setSelectedNodeKey(node.key); setSelectedEdgeKey(''); }} onDoubleClick={() => openIntelligence(node.key)} style={{ cursor: 'pointer' }} opacity={(relationFocus ? 1 : .18) * (!drilldownActive || drilldownKeys.has(node.key) ? 1 : .12)} filter={selected || ai || onPath || node.crownJewel || node.risk.score >= 85 ? 'url(#mob21Glow)' : undefined}>
                <title>{tooltip}</title>
                {(ai || onPath) && <rect x="-7" y="-7" width={node.width + 14} height={node.height + 14} rx="16" fill={aiStatus === 'confirmed_compromise' ? 'rgba(244,63,94,.09)' : onPath ? 'rgba(250,204,21,.04)' : 'rgba(217,70,239,.07)'} stroke={onPath && !ai ? 'rgba(250,204,21,.92)' : aiStatus === 'confirmed_compromise' ? 'rgba(251,113,133,.98)' : 'rgba(232,121,249,.94)'} strokeWidth="2.4" strokeDasharray="5 4"><animate attributeName="stroke-dashoffset" values="0;-36" dur="1.35s" repeatCount="indefinite" /></rect>}
                {node.crownJewel && canCrown && <rect x="-4" y="-4" width={node.width + 8} height={node.height + 8} rx="14" fill="rgba(234,179,8,.025)" stroke="rgba(250,204,21,.30)" strokeWidth="1" />}
                <rect width={node.width} height={node.height} rx="11" fill={fill} stroke={selected ? 'rgb(34,211,238)' : baseStroke} strokeWidth={selected ? 2.6 : status !== 'unchanged' || node.crownJewel ? 2 : 1.3} strokeDasharray={Boolean(node.properties['declaredOnly']) ? '5 3' : undefined} />
                <text x="9" y="14" fill="rgb(100,116,139)" fontSize="7.3" fontWeight="800">{node.type.replaceAll('_',' ').toUpperCase().slice(0,18)}</text>
                <text x="9" y="30" fill="rgb(226,232,240)" fontSize="9.5" fontWeight="800">{node.label.length > (node.width > 150 ? 22 : 18) ? `${node.label.slice(0, node.width > 150 ? 21 : 17)}…` : node.label}</text>
                {canProductCoverage && node.estate?.providers.slice(0, 6).map((provider, index) => <circle key={provider} cx={node.width - 13 - index * 9} cy={node.height - 9} r="2.5" fill={providerMeta(provider).dot}><title>{providerMeta(provider).label}</title></circle>)}
                <text x="9" y={node.height - 8} fill={monitoring === 'blind' ? 'rgb(251,113,133)' : monitoring === 'partial' ? 'rgb(251,191,36)' : 'rgb(52,211,153)'} fontSize="7" fontWeight="800">{monitoring === 'not_applicable' ? 'ARCH' : `${monitoring.toUpperCase()} ${node.monitoring.coveragePercent}%`}</text>
                <text x={node.width - 9} y="14" textAnchor="end" fill={node.risk.score >= 65 ? 'rgb(253,186,116)' : 'rgb(148,163,184)'} fontSize="8" fontWeight="900">R{node.risk.score}</text>
                {canvasMode === 'intelligence' && node.hop !== undefined && node.hop > 0 && <text x={node.width - 9} y={node.height - 8} textAnchor="end" fill="rgb(167,139,250)" fontSize="6.8" fontWeight="900">H{node.hop}</text>}
                {node.crownJewel && canCrown && canvasMode !== 'intelligence' && <text x={node.width - 9} y={node.height - 8} textAnchor="end" fill="rgb(250,204,21)" fontSize="6.8" fontWeight="900">CROWN</text>}
                {status !== 'unchanged' && <text x={node.width - 9} y="29" textAnchor="end" fill={status === 'new' ? 'rgb(52,211,153)' : 'rgb(251,191,36)'} fontSize="6.5" fontWeight="900">{status.toUpperCase()}</text>}
              </g>;
            })}
          </svg>

          <div className="pointer-events-none absolute bottom-4 left-4 rounded-lg border border-white/10 bg-slate-950/85 px-3 py-2 text-[10px] text-slate-500 backdrop-blur">{canvasMode === 'intelligence' ? `Intelligence graph · ${layout.nodes.length} nodes / ${layout.edges.length} paths · ${graphDepth} hop depth` : lens === 'sentinel' ? `Sentinel lens · ${sentinelSeedCount} seeds · ${layout.nodes.length} rendered entities / ${layout.edges.length} paths${layout.clipped ? ` · ${layout.clipped} summarized` : ''}` : `Rendered ${layout.nodes.length} nodes / ${layout.edges.length} paths${layout.clipped ? ` · ${layout.clipped} summarized` : ''}`} · zoom {Math.round(zoom * 100)}%</div>
          {immersive && <div className="pointer-events-none absolute bottom-4 right-4 rounded-lg border border-violet-400/20 bg-violet-950/45 px-3 py-2 text-[9px] font-black uppercase tracking-[.16em] text-violet-200 backdrop-blur">{canvasMode === 'intelligence' ? 'MoB Intelligence Graph · evidence provenance retained' : 'MoB immersive architecture · semantic evidence arcs'}</div>}

          {canMinimap && showMinimap && canvasMode === 'architecture' && <div className="absolute right-4 top-4 overflow-hidden rounded-xl border border-white/10 bg-slate-950/88 p-2 shadow-xl backdrop-blur"><div className="mb-1 flex items-center gap-1 text-[8px] font-black uppercase text-slate-500"><MapIcon size={9} />Minimap</div><svg width="190" height="104" viewBox={`0 0 ${BASE_WIDTH} ${BASE_HEIGHT}`} onPointerDown={(event) => { event.stopPropagation(); minimapClick(event); }} style={{ cursor: 'crosshair' }}>{snapshot.zones.map((zone) => { const geometry = focusedZoneId === zone.id ? FOCUSED_GEOMETRY : ZONE_GEOMETRY[zone.id]; if (!geometry || (focusedZoneId && zone.id !== focusedZoneId)) return null; return <rect key={zone.id} x={geometry.x} y={geometry.y} width={geometry.width} height={geometry.height} rx="18" fill={miniZoneFill(zone.id)} opacity=".19" stroke={miniZoneFill(zone.id)} strokeWidth="7" />; })}<rect x={viewX} y={viewY} width={viewWidth} height={viewHeight} fill="rgba(34,211,238,.03)" stroke="rgba(103,232,249,.95)" strokeWidth="9" /></svg></div>}
        </div>

        <aside className={`border-l border-slate-800 bg-slate-950/96 p-4 ${isFullscreen ? 'min-h-0 overflow-y-auto' : ''}`}>
          {selectedEdge ? <EdgeDossier edge={selectedEdge} nodes={snapshot.nodes} generatedAt={snapshot.generatedAt} onClose={() => setSelectedEdgeKey('')} /> : selectedNode ? <NodeDossier node={selectedNode} edges={selectedEdges} canProvenance={canProvenance} canFocus={canFocus} canIntelGraph={canIntelGraph} canCrownPath={canCrownPath} canProductCoverage={canProductCoverage} canFreshness={canFreshness} canSentinelIncidents={canSentinelIncidents} focusDepth={focusDepth} onFocus={enableFocus} onClearFocus={() => setFocusDepth(0)} onIntelligence={() => openIntelligence(selectedNode.key)} onCrownPaths={calculateCrownPaths} onNavigate={onNavigate} customerId={customerId} snapshotId={snapshot.id} /> : <div className="flex h-full min-h-[350px] flex-col items-center justify-center text-center"><Eye className="h-8 w-8 text-slate-700" /><div className="mt-3 text-sm font-bold text-slate-300">Select an architecture entity</div><p className="mt-2 max-w-[250px] text-[10px] leading-5 text-slate-600">Click a node for its dossier. Double-click a node for Intelligence Graph. Click a summarized architecture zone to expand it.</p></div>}
        </aside>
      </div>
    </section>
  );
}

function NodeDossier({ node, edges, canProvenance, canFocus, canIntelGraph, canCrownPath, canProductCoverage, canFreshness, canSentinelIncidents, focusDepth, onFocus, onClearFocus, onIntelligence, onCrownPaths, onNavigate, customerId, snapshotId }: {
  node: MobNode;
  edges: MobEdge[];
  canProvenance: boolean;
  canFocus: boolean;
  canIntelGraph: boolean;
  canCrownPath: boolean;
  canProductCoverage: boolean;
  canFreshness: boolean;
  canSentinelIncidents: boolean;
  focusDepth: 0 | 1 | 2 | 3;
  onFocus: (depth: 1 | 2 | 3) => void;
  onClearFocus: () => void;
  onIntelligence: () => void;
  onCrownPaths: () => void;
  onNavigate?: (tabId: string, customerId?: string) => void;
  customerId: string;
  snapshotId: string;
}) {
  const Icon = nodeIcon(node.type);
  return <div className="space-y-4">
    <div><div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-wider text-cyan-300"><Icon size={13} />{node.type.replaceAll('_',' ')}</div><div className="mt-1 break-words text-sm font-black text-slate-100">{node.label}</div></div><div className={`shrink-0 rounded border px-2 py-1 text-[9px] font-black ${riskClass(node.risk.score)} border-current/25 bg-current/5`}>R{node.risk.score}</div></div>{onNavigate && <button type="button" onClick={() => onNavigate('entity_360', customerId)} className="mt-2 text-[9px] font-bold text-cyan-400 hover:text-cyan-200">Open Entity 360 →</button>}</div>
    <div className="flex flex-wrap gap-1.5">{canIntelGraph && <button type="button" onClick={onIntelligence} className="inline-flex items-center gap-1 rounded-lg border border-violet-500/25 bg-violet-500/8 px-2.5 py-1.5 text-[9px] font-bold text-violet-200"><GitBranch size={11} />Intelligence Graph</button>}{canCrownPath && <button type="button" onClick={onCrownPaths} className="inline-flex items-center gap-1 rounded-lg border border-yellow-500/25 bg-yellow-500/8 px-2.5 py-1.5 text-[9px] font-bold text-yellow-200"><Route size={11} />Paths to Crown Jewels</button>}</div>
    <NativeResponseActions customerId={customerId} entityType={node.type} entityValue={node.label} source="mob" mobSnapshotId={snapshotId} mobNodeKey={node.key} compact />
    {canFocus && <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="flex items-center justify-between"><div className="text-[9px] font-black uppercase tracking-wider text-slate-500"><Crosshair size={10} className="mr-1 inline" />Focus neighbourhood</div>{focusDepth > 0 && <button type="button" onClick={onClearFocus} className="text-[8px] font-bold text-slate-600">Clear</button>}</div><div className="mt-2 flex gap-1">{([1,2,3] as const).map((depth) => <button key={depth} type="button" onClick={() => onFocus(depth)} className={`flex-1 rounded border px-2 py-1.5 text-[8px] font-black ${focusDepth === depth ? 'border-cyan-400/35 bg-cyan-500/10 text-cyan-200' : 'border-slate-700 text-slate-500'}`}>{depth} hop</button>)}</div></div>}
    <div className="grid grid-cols-2 gap-2"><Mini label="Zone" value={node.zone.replaceAll('_',' ')} /><Mini label="Threat" value={node.threatState.replaceAll('_',' ')} /><Mini label="Criticality" value={node.criticality ?? '-'} /><Mini label="Last seen" value={formatTime(node.lastSeen)} /></div>
    {node.estate && canProductCoverage && <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Platform coverage</div><div className="mt-2 flex flex-wrap gap-1.5">{node.estate.providers.map((provider) => { const meta = providerMeta(provider); return <span key={provider} className={`rounded border px-2 py-1 text-[8px] font-black uppercase ${meta.chip}`} title={meta.label}>{meta.short} · {meta.label}</span>; })}</div><div className="mt-2 text-[8px] text-slate-600">Canonical confidence {node.estate.confidence}% · {node.estate.evidenceCount} evidence · {node.estate.matchStrategy.replaceAll('_',' ')}</div></div>}
    {node.estate && canFreshness && <div className={`rounded-xl border p-3 ${freshnessClass(node.estate.freshness)}`}><div className="flex items-center justify-between"><div className="text-[9px] font-black uppercase tracking-wider">Estate freshness</div><span className="text-[9px] font-black uppercase">{node.estate.freshness}</span></div><div className="mt-1 text-[9px] opacity-85">Last canonical observation: {formatTime(node.estate.lastObservedAt)}{node.estate.freshnessAgeMinutes != null ? ` · ${node.estate.freshnessAgeMinutes}m ago` : ''}</div></div>}
    {node.type === 'incident' && node.estate && canSentinelIncidents && <div className="rounded-xl border border-rose-400/25 bg-rose-500/8 p-3"><div className="text-[9px] font-black uppercase tracking-wider text-rose-300">Microsoft Sentinel incident</div><div className="mt-2 grid grid-cols-2 gap-2"><Mini label="Number" value={String((node.properties['canonicalIdentifiers'] as Record<string, unknown> | undefined)?.['sentinelIncidentNumber'] ?? node.properties['sentinelIncidentNumber'] ?? '-')} /><Mini label="Severity" value={String(node.properties['severity'] ?? '-')} /><Mini label="Status" value={String(node.properties['status'] ?? '-')} /><Mini label="Last modified" value={formatTime(String(node.properties['lastModifiedTimeUtc'] ?? node.estate.lastObservedAt ?? ''))} /></div></div>}
    <div className={`rounded-xl border p-3 ${monitoringClass(node.monitoring.status)}`}><div className="flex items-center justify-between"><div className="text-[9px] font-black uppercase tracking-wider">Monitoring</div><div className="text-sm font-black">{node.monitoring.coveragePercent}%</div></div><div className="mt-1 text-[10px] font-bold">{node.monitoring.status.replaceAll('_',' ')}</div>{node.monitoring.signals.length > 0 && <div className="mt-2 text-[9px] opacity-80">Signals: {node.monitoring.signals.join(' · ')}</div>}{node.monitoring.gaps.length > 0 && <div className="mt-2 text-[9px] opacity-90">Gaps: {node.monitoring.gaps.join(' · ')}</div>}</div>
    {node.crownJewel && <div className="rounded-xl border border-yellow-400/30 bg-yellow-500/8 p-3"><div className="text-[9px] font-black uppercase tracking-wider text-yellow-300">Crown jewel</div><div className="mt-1 text-[10px] leading-4 text-yellow-100/80">Approved Critical asset from SOC Digital Twin. {Boolean(node.properties['declaredOnly']) ? 'This asset is declared but absent from current FoB evidence, so MoB exposes it as an architecture/monitoring blind spot.' : 'This crown jewel is correlated to persisted FoB evidence.'}</div></div>}
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Why is this risky?</div><div className="mt-2 space-y-1.5">{node.risk.contributors.length ? node.risk.contributors.map((item, index) => <div key={index} className="text-[9px] leading-4 text-slate-400">• {item}</div>) : <div className="text-[9px] text-slate-600">No additive MoB risk contributors.</div>}</div><div className="mt-2 border-t border-slate-800 pt-2 text-[9px] text-slate-600">Underlying FoB base risk: {node.risk.baseScore}</div></div>
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Evidence sources</div><div className="mt-2 flex flex-wrap gap-1">{node.sourceTables.length ? node.sourceTables.map((source) => <span key={source} className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-[8px] text-slate-400">{source}</span>) : <span className="text-[9px] text-rose-300">No source table observed</span>}</div></div>
    {canProvenance && <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Relationship provenance</div><div className="mt-2 space-y-1.5">{edges.slice(0, 14).map((edge) => <div key={edge.key} className="flex items-center justify-between gap-2 text-[9px]"><span className="min-w-0 truncate text-slate-400">{edge.label}</span><span className={`shrink-0 rounded border px-1.5 py-0.5 font-bold uppercase ${edge.provenance === 'observed' ? 'border-cyan-500/25 text-cyan-300' : edge.provenance === 'declared' ? 'border-slate-600 text-slate-400' : 'border-fuchsia-500/25 text-fuchsia-300'}`}>{edge.provenance}</span></div>)}{edges.length === 0 && <div className="text-[9px] text-slate-600">No persisted relationships touch this node.</div>}</div></div>}
    <details className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><summary className="cursor-pointer text-[9px] font-black uppercase tracking-wider text-slate-500">Normalized properties</summary><div className="mt-3 space-y-1.5">{safeEntries(node.properties).map(([key,value]) => <div key={key} className="grid grid-cols-[105px_1fr] gap-2 text-[8px]"><span className="break-all font-bold text-slate-600">{key}</span><span className="break-all font-mono text-slate-400">{value}</span></div>)}</div></details>
  </div>;
}

function EdgeDossier({ edge, nodes, generatedAt, onClose }: { edge: MobEdge; nodes: MobNode[]; generatedAt: string | null; onClose: () => void }) {
  const from = nodes.find((node) => node.key === edge.from);
  const to = nodes.find((node) => node.key === edge.to);
  const activity = activityState(edge, generatedAt);
  const kind = relationKind(edge);
  return <div className="space-y-4">
    <div className="flex items-start justify-between gap-3"><div><div className="text-[9px] font-black uppercase tracking-wider text-violet-300"><GitBranch size={11} className="mr-1 inline" />Relationship evidence</div><div className="mt-1 text-sm font-black text-slate-100">{edge.label}</div></div><button type="button" onClick={onClose} className="rounded border border-slate-700 p-1 text-slate-500"><X size={12} /></button></div>
    <div className="rounded-xl border border-slate-800 bg-slate-900/55 p-3"><div className="text-[8px] font-black uppercase text-slate-600">Path</div><div className="mt-2 text-[10px] font-bold text-cyan-200">{from?.label ?? edge.from}</div><div className="my-1 text-[9px] text-slate-600">↓ {edge.label}</div><div className="text-[10px] font-bold text-violet-200">{to?.label ?? edge.to}</div></div>
    <div className="grid grid-cols-2 gap-2"><Mini label="Kind" value={RELATION_LABELS[kind]} /><Mini label="Provenance" value={edge.provenance} /><Mini label="Activity" value={activity} /><Mini label="Confidence" value={`${edge.confidence}%`} /><Mini label="Evidence" value={String(edge.evidenceCount)} /><Mini label="Last seen" value={formatTime(edge.lastSeen)} /></div>
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3"><div className="text-[9px] font-black uppercase tracking-wider text-slate-500">Evidence lineage</div><div className="mt-2 flex flex-wrap gap-1">{edge.sourceTables.length ? edge.sourceTables.map((source) => <span key={source} className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-[8px] text-slate-400">{source}</span>) : <span className="text-[9px] text-slate-600">No source table label retained.</span>}</div></div>
    <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 text-[9px] leading-5 text-slate-500"><b className="text-slate-300">Visual semantics:</b> observed relationships are solid, declared relationships dotted, derived relationships dashed. Fast pulse means activity within roughly one hour of this snapshot; slow pulse means activity within roughly 24 hours; static means historical or timeless architecture context.</div>
  </div>;
}

function Mini({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-2"><div className="text-[8px] font-bold uppercase tracking-wider text-slate-600">{label}</div><div className="mt-1 truncate text-[9px] font-semibold capitalize text-slate-300" title={value}>{value}</div></div>;
}
